import '../styles/subscriptionWizardV2.css';
import * as mdc from "material-components-web";
import UrlHelpers from "./url";
import {i18n} from "./i18n";
import {createSystemBackground, dvLogo, successFlagBackground} from "./icons";
import {TenantTerminationState} from "./tenant";
import {Escaping} from "./escaping";


export class SubscriptionWizardV2 {
    private readonly _i18n: i18n = new i18n();

    private readonly _isNewUser: boolean;
    private readonly _isOrganizationLimitExceeded: boolean;
    private readonly _isSysAdmin: boolean;
    private readonly _gtmActive: boolean;

    private readonly _isBaseAppSubscription: boolean;
    private readonly _withPreselectedOrganization: boolean;
    private readonly _orderModeActive: boolean;

    private _wizardPageElements: { [key: string]: HTMLDivElement } = {}

    private _wizardPageHistory: WizardPages[] = [];
    private _currentPage: WizardPages;
    private _entrancePage: WizardPages;

    //change only via setter function 'patchWizardState'
    private _wizardState: WizardState;

    private _mdcTextFields: { [id: string]: mdc.textField.MDCTextField } = {};
    private _mdcSelects: { [id: string]: mdc.select.MDCSelect } = {};
    private _mdcLists: { [id: string]: mdc.list.MDCList } = {};
    private _mdcProgressBars: { [id: string]: mdc.linearProgress.MDCLinearProgress } = {};
    private _mdcSnackbars: { [id: string]: mdc.snackbar.MDCSnackbar } = {};
    private _mdcDialogs: { [id: string]: mdc.dialog.MDCDialog } = {};
    private _mdcMenus: { [id: string]: mdc.menu.MDCMenu } = {};
    private _mdcCheckboxes: { [id: string]: mdc.checkbox.MDCCheckbox } = {};
    private _mdcRadios: { [id: string]: mdc.radio.MDCRadio } = {};

    private _createNewOrganization: boolean;
    private _createNewTenant: boolean;

    private _delayTimer: number;

    //set only via setter func 'setSubscriptionSuccessful'
    private _subscriptionSuccessful: boolean;

    constructor() {
        console.debug("constructor start");
        const appName = UrlHelpers.getMetaData('appName');
        if (!appName) {
            console.info("app name missing in query");
        }
        const subscriptionOrder = UrlHelpers.getQueryParameter(window.location.href, 'soid');
        if (!subscriptionOrder) {
            console.info("subscription order id missing in query");
        }

        this.collectPages();
        this.setBackgroundImages();

        this._isSysAdmin = !!UrlHelpers.getMetaData("isSysAdmin");
        this._isNewUser = !!UrlHelpers.getMetaData("isNewUser");
        this._isOrganizationLimitExceeded = !!UrlHelpers.getMetaData("isOrganizationLimitExceeded");
        this._gtmActive = !!UrlHelpers.getMetaData("gtmActive");
        this._orderModeActive = !!subscriptionOrder

        const oid = UrlHelpers.getQueryParameter(window.location.href, 'oid');

        this._isBaseAppSubscription = appName == 'basis';
        this._withPreselectedOrganization = !!oid && this._isBaseAppSubscription;


        this._createNewTenant = this._isNewUser;
        this._createNewOrganization = this._isNewUser;

        this._wizardState = this.getEmptyState();
        this._wizardState.app = appName;
        if (this._withPreselectedOrganization) {
            this._wizardState.organization = oid;
            this._createNewOrganization = false;
        }
        if (this._orderModeActive) {
            this._wizardState.order = subscriptionOrder;
            !!UrlHelpers.getMetaData("orderFirstname") ? this._wizardState.customerInfo.firstname = UrlHelpers.getMetaData("orderFirstname") : console.info("no customer firstname found in order");
            !!UrlHelpers.getMetaData("orderLastname") ? this._wizardState.customerInfo.lastname = UrlHelpers.getMetaData("orderLastname") : console.info("no customer lastname found in order");
            !!UrlHelpers.getMetaData("orderCompany") ? this._wizardState.customerInfo.company = UrlHelpers.getMetaData("orderCompany") : console.info("no customer company found in order");
            Array.apply(null, document.querySelectorAll('.order-article')).forEach((el: HTMLElement) => {
                this._wizardState.orderedApps.push(el.dataset.appname);
            });
        }

        this.instantiateMaterialComponents();

        this.loadTenantList(this.getWizardDataUrl(), this.mergeSelectSystemData,
            (error) => {
                console.error(`loading wizard data failed ${error}`)
                this.showNotification(this._i18n.getTranslation('subscriptionWizardV2_loadWizardDataFailed_error_message'), true);
            });

        this.loadOrganizationList(this.getOrganizationListUrl(), this.mergeCreateSystemData,
            (error) => {
                console.error(`loading organization data failed ${error}`)
                this.showNotification(this._i18n.getTranslation('subscriptionWizardV2_loadWizardDataFailed_error_message'), true);
            });


        this.setSubscriptionSuccessful(false);

        this.setupCreateSystem();
        this.setupCustomerInfo();
        this.setupShoppingCart();
        this.setupSelectSystem();
        this.setupChangeDomainDialog();
        this.setupChangeCustomerInfoDialog();

        this.setEntrancePage();
        this._currentPage = this._entrancePage;
        this.showCurrentPage();

        this.renderFromState();

        window.addEventListener('load', () => {
            this.instantiateMaterialComponents();
            this.showWizard();
        });

        window.addEventListener('beforeunload', function (e) {
            e.preventDefault();
            window.localStorage.removeItem('createTenantForOrganization');
        });


        const selectSystemMoreButton = <HTMLButtonElement>document.getElementById('subscriptionWizardV2SelectSystem_orga_selection_list_btn_more');
        if (selectSystemMoreButton) {
            selectSystemMoreButton.addEventListener('click', this.handleSelectSystemLoadMoreButton);
        }
    }

    private loadTenantList = (url: string, cbSuccess: (data: wizardData) => void, cbError: (e) => void) => {

        this.openSelectSystemListProgressbar();
        const req = new Request(url, {
            method: 'GET',
            headers: {
                'accept': 'application/hal+json',
            },
            credentials: 'same-origin'
        });

        fetch(req)
            .then((response) => {
                this.closeSelectSystemListProgressbar();
                if (response.ok) {
                    response.json()
                        .then((data: wizardData) => {
                            cbSuccess(data);
                        })
                        .catch(reason => {
                            console.error(`load wizard data: cannot get json from response: ${reason}`);
                            cbError(reason);
                        })
                } else {
                    response.text().then(value => {
                        console.error(`load wizard data failed with status ${response.status} - ${response.statusText}: ${value}`);
                        cbError(`load wizard data failed`);
                    }).catch(reason => {
                        console.error(`load wizard data failed with status ${response.status} - ${response.statusText}: ${reason}`);
                        cbError(`load wizard data failed`);
                    })
                }
            })
            .catch(reason => {
                this.closeSelectSystemListProgressbar();
                cbError(reason)
            });
    };

    private loadOrganizationList = (url: string, cbSuccess: (data: organizationList) => void, cbError: (e) => void) => {
        this.openCreateSystemListProgressbar();
        const req = new Request(url, {
            method: 'GET',
            headers: {
                'accept': 'application/hal+json',
            },
            credentials: 'same-origin'
        });

        fetch(req)
            .then((response) => {
                this.closeCreateSystemListProgressbar();
                if (response.ok) {
                    response.json()
                        .then((data: organizationList) => {
                            cbSuccess(data);
                        })
                        .catch(reason => {
                            console.error(`load organization data: cannot get json from response: ${reason}`);
                            cbError(reason);
                        })
                } else {
                    response.text().then(value => {
                        console.error(`load organization data failed with status ${response.status} - ${response.statusText}: ${value}`);
                        cbError(`load organization data failed`);
                    }).catch(reason => {
                        console.error(`load organization data failed with status ${response.status} - ${response.statusText}: ${reason}`);
                        cbError(`load organization data failed`);
                    })
                }
            })
            .catch(reason => {
                this.closeCreateSystemListProgressbar();
                cbError(reason)
            });
    };

    private mergeSelectSystemData = (data: wizardData): void => {
        if (!data) {
            console.log("mergeSelectSystemData: data is empty");
            return;
        }

        const current = this.readSelectSystemDataFromElement();
        current._links = data._links;
        if (!data.organizations) {
            console.log("mergeSelectSystemData: organizations is empty");
            this.writeSelectSystemDataToElement(current);
            return;
        }
        data.organizations.forEach((orga) => {
            if (!orga.tenants || orga.tenants.length < 1) {
                return;
            }
            const i = current.organizations.findIndex(o => o.organizationId == orga.organizationId);
            if (i == -1) {
                current.organizations.push(orga);
                return;
            }
            const currentTenants: wizardTenant[] = current.organizations[i].tenants ? current.organizations[i].tenants : [];
            orga.tenants.forEach((tenant) => {
                const ti = currentTenants.findIndex((t) => t.tenantId == tenant.tenantId);
                if (ti == -1) {
                    currentTenants.push(tenant);
                    return;
                }
            });
            current.organizations[i].tenants = currentTenants;
            current.organizations[i].tenants.sort(function (a, b) {
                const tenantA = a.tenantId.toLowerCase();
                const tenantB = b.tenantId.toLowerCase();
                if (tenantA < tenantB) {
                    return -1;
                }
                if (tenantA > tenantB) {
                    return 1;
                }
                return 0;
            });
        });
        current.organizations.sort(function (a, b) {
            const tenantA = a.organizationId.toLowerCase();
            const tenantB = b.organizationId.toLowerCase();
            if (tenantA < tenantB) {
                return -1;
            }
            if (tenantA > tenantB) {
                return 1;
            }
            return 0;
        });
        this.writeSelectSystemDataToElement(current);
    };

    private mergeCreateSystemData = (data: organizationList): void => {
        if (!data) {
            console.log("mergeCreateSystemData: data is empty");
            return;
        }

        const parsedData = this.parseOrganizationToWizardData(data)

        const current = this.readCreateSystemDataFromElement();
        current._links = parsedData._links;
        if (!parsedData.organizations) {
            console.log("mergeCreateSystemData: organizations is empty");
            this.writeCreateSystemDataToElement(current);
            return
        }
        parsedData.organizations.forEach((orga: wizardOrga) => {
            const i = current.organizations.findIndex(o => o.organizationId == orga.organizationId);
            if (i == -1) {
                current.organizations.push(orga);
                return;
            }
        });
        current.organizations.sort(function (a, b) {
            const tenantA = a.organizationId.toLowerCase();
            const tenantB = b.organizationId.toLowerCase();
            if (tenantA < tenantB) {
                return -1;
            }
            if (tenantA > tenantB) {
                return 1;
            }
            return 0;
        });
        this.writeCreateSystemDataToElement(current);
    };

    private parseOrganizationToWizardData = (ol: organizationList): wizardData => {
        const wizOrgs: wizardOrga[] = ol.organizations.map((v) => {
            return {
                organizationId: v.id,
                organizationName: v.name
            } as wizardOrga
        })
        return {
            _links: ol._links,
            organizations: wizOrgs
        } as wizardData
    };

    private setEntrancePage = (): void => {
        this._entrancePage = this._isNewUser || this._isBaseAppSubscription ? WizardPages.CustomerInfo : WizardPages.SelectSystem;
    };

    private setBackgroundImages = (): void => {
        const setBgImage = (e: HTMLDivElement, url: string) => {
            e.style.backgroundImage = `url('${url}')`;
        };
        const brandings = Array.apply(null, document.querySelectorAll('.dv-branding'));
        brandings.forEach((b: HTMLDivElement) => {
            setBgImage(b, dvLogo);
        });

        const createSystem = <HTMLDivElement>document.querySelector('.create-system-bg-image');
        if (createSystem) {
            setBgImage(createSystem, createSystemBackground);
        }
        const createSystemPlaceholder = <HTMLImageElement>document.getElementById('create-system-bg-image-placeholder');
        if (createSystemPlaceholder) {
            createSystemPlaceholder.src = createSystemBackground;
        }

        const success = <HTMLDivElement>document.querySelector('.final-bg-image');
        if (success) {
            setBgImage(success, successFlagBackground);
        }
    }

    private patchWizardState(value: WizardState): void {
        console.debug("patch state with:", value);
        const nullOrUndefined = (value: any): boolean => {
            return value == null || value == undefined;
        }
        this._wizardState = {
            domain: !nullOrUndefined(value.domain) ? value.domain.trim() : this._wizardState.domain,
            organization: !nullOrUndefined(value.organization) && value.organization.trim() !== "new" ? value.organization.trim() : this._wizardState.organization,
            organizationName: !nullOrUndefined(value.organizationName) ? value.organizationName.trim() : this._wizardState.organizationName,
            tenant: !nullOrUndefined(value.tenant) ? value.tenant.trim() : this._wizardState.tenant,
            app: !nullOrUndefined(value.app) ? value.app.trim() : this._wizardState.app,
            order: !nullOrUndefined(value.order) ? value.order.trim() : this._wizardState.order,
            orderedApps: !nullOrUndefined(value.orderedApps) ? value.orderedApps : this._wizardState.orderedApps,
            customerInfo: {
                firstname: value.customerInfo && !nullOrUndefined(value.customerInfo.firstname) ? value.customerInfo.firstname.trim() : this._wizardState.customerInfo.firstname,
                lastname: value.customerInfo && !nullOrUndefined(value.customerInfo.lastname) ? value.customerInfo.lastname.trim() : this._wizardState.customerInfo.lastname,
                phone: value.customerInfo && !nullOrUndefined(value.customerInfo.phone) ? value.customerInfo.phone.trim() : this._wizardState.customerInfo.phone,
                country: value.customerInfo && !nullOrUndefined(value.customerInfo.country) ? value.customerInfo.country.trim() : this._wizardState.customerInfo.country,
                company: value.customerInfo && !nullOrUndefined(value.customerInfo.company) ? value.customerInfo.company.trim() : this._wizardState.customerInfo.company,
                employeesCount: value.customerInfo && !nullOrUndefined(value.customerInfo.employeesCount) ? value.customerInfo.employeesCount : this._wizardState.customerInfo.employeesCount
            }
        } as WizardState;
        console.debug("new state is:", this._wizardState);
        this.renderFromState();
    }

    private renderFromState(): void {
        this.renderCreateSystem();
        this.renderSelectSystem();
        this.renderCustomerInfo();
        this.renderShoppingCart();
        this.renderFinal();
        this.renderChangeDomainDialog();
        this.renderChangeCustomerInfoDialog()
    }

    private showWizard = (): void => {
        console.debug("show wizard contents");
        const wizardId = 'subscriptionWizardV2';
        const content = <HTMLElement>document.getElementById(wizardId);
        if (!content) {
            console.log('cannot find element with class', wizardId);
            return;
        }
        content.style.display = '';
    }

    private collectPages = (): void => {
        console.debug("collecting wizard pages");
        const pages = Array.apply(null, document.querySelectorAll('.subscriptionWizardV2__step'));
        pages.forEach((p: HTMLDivElement) => {
            this._wizardPageElements[p.id] = p;
        })
    };

    private showPage = (pageId: WizardPages): void => {
        if (!this._wizardPageElements.hasOwnProperty(pageId)) {
            //todo: error toast
            console.error(`page with id '${pageId}' not found`);
            return;
        }

        const currentClass = "subscriptionWizardV2__step--current";
        const hideClass = "subscriptionWizardV2__step--hidden";

        for (const wizardPagesKey in this._wizardPageElements) {
            const page = <HTMLDivElement>this._wizardPageElements[wizardPagesKey];
            if (wizardPagesKey != pageId) {
                page.classList.remove(currentClass);
                page.classList.add(hideClass);
                continue;
            }
            page.classList.remove(hideClass);
            page.classList.add(currentClass);
        }
        this.dispatchGooglePageView(pageId);
    }

    private showCurrentPage(): void {
        this.showPage(this._currentPage);
    }

    private goToPage = (page: WizardPages): void => {
        this._wizardPageHistory.push(this._currentPage);
        this.renderFromState();
        this._currentPage = page;
        this.showCurrentPage();
    };

    private goToPreviousPage = (): void => {
        this._currentPage = this._wizardPageHistory.pop();
        this.showCurrentPage();
    }

    private setSubscriptionSuccessful = (success: boolean): void => {
        this._subscriptionSuccessful = success;
        this.renderFinal();
    };

    private instantiateMaterialComponents = (): void => {
        console.debug("instantiate material components");
        this.instantiateMaterialTextFields();
        this.instantiateMaterialSelects();
        this.instantiateMaterialLists();
        this.instantiateMaterialButtons();
        this.instantiateMaterialLinearProgress();
        this.instantiateMaterialSnackbars();
        this.instantiateMaterialDialogs();
        this.instantiateMaterialMenus();
        this.instantiateMaterialCheckboxes();
        this.instantiateMaterialRadios();
    }

    private instantiateMaterialTextFields = (): void => {
        const textfields = document.querySelectorAll('.mdc-text-field');
        Array.apply(null, textfields).forEach((textfield) => {
            const mdcTextfield = mdc.textField.MDCTextField.attachTo(textfield);
            //take input or textarea id if wrapping div has no id itself
            if (!textfield.id) {
                const inputs = textfield.getElementsByTagName('input');
                if (inputs.length > 0 && inputs[0].id) {
                    this._mdcTextFields[inputs[0].id] = mdcTextfield;
                    return
                }
                const areas = textfield.getElementsByTagName('textarea');
                if (areas.length > 0 && areas[0].id) {
                    this._mdcTextFields[areas[0].id] = mdcTextfield;
                    return
                }
                return
            }
            this._mdcTextFields[textfield.id] = mdcTextfield;
        });
    };

    private instantiateMaterialSelects = (): void => {
        const selects = document.querySelectorAll('.mdc-select');
        Array.apply(null, selects).forEach((uList) => {
            const mdcSelect = mdc.select.MDCSelect.attachTo(uList);
            if (uList.id) {
                this._mdcSelects[uList.id] = mdcSelect;
            }
        });
    };

    private instantiateMaterialLists = (): void => {
        const uLists = document.querySelectorAll('.mdc-list');
        Array.apply(null, uLists).forEach((uList) => {
            const mdcList = mdc.list.MDCList.attachTo(uList);
            if (uList.id) {
                this._mdcLists[uList.id] = mdcList;
            }
        });
    };

    private instantiateMaterialButtons = (): void => {
        const buttons = document.querySelectorAll('.mdc-button');
        Array.apply(null, buttons).forEach((button) => {
            mdc.ripple.MDCRipple.attachTo(button);
        });
    };

    private instantiateMaterialLinearProgress = (): void => {
        const progressBars = document.querySelectorAll('.mdc-linear-progress');
        Array.apply(null, progressBars).forEach((pBar: HTMLElement) => {
            const mdcBar = mdc.linearProgress.MDCLinearProgress.attachTo(pBar);
            if (pBar.id) {
                this._mdcProgressBars[pBar.id] = mdcBar;
            }
        });
    };

    private instantiateMaterialDialogs = (): void => {
        const dialogs = document.querySelectorAll('.mdc-dialog');
        Array.apply(null, dialogs).forEach((d: HTMLElement) => {
            const mdcDialog = mdc.dialog.MDCDialog.attachTo(d);
            if (d.id) {
                this._mdcDialogs[d.id] = mdcDialog;
            }
        });
    };

    private instantiateMaterialMenus = (): void => {
        const menus = document.querySelectorAll('.mdc-menu');
        Array.apply(null, menus).forEach((m: HTMLElement) => {
            const mdcMenu = mdc.menu.MDCMenu.attachTo(m);
            if (m.id) {
                this._mdcMenus[m.id] = mdcMenu;
            }
        });
    };

    private instantiateMaterialRadios = (): void => {
        const radios = document.querySelectorAll('.mdc-radio');
        Array.apply(null, radios).forEach((r: HTMLElement) => {
            const mdcRadio = mdc.radio.MDCRadio.attachTo(r);
            if (r.id) {
                this._mdcRadios[r.id] = mdcRadio;
                return;
            }
            const inputs = r.getElementsByTagName('input');
            if (inputs.length > 0 && inputs[0].id) {
                this._mdcRadios[inputs[0].id] = mdcRadio;
                return;
            }
        });
    };

    private instantiateMaterialSnackbars = (): void => {
        const snackbars = document.querySelectorAll('.mdc-snackbar');
        Array.apply(null, snackbars).forEach((snackbar) => {
            const mdcSnackbar = mdc.snackbar.MDCSnackbar.attachTo(snackbar);
            if (snackbar.id) {
                this._mdcSnackbars[snackbar.id] = mdcSnackbar;
            }
        });
    };

    private instantiateMaterialCheckboxes = (): void => {
        const checkboxes = document.querySelectorAll('.mdc-checkbox');
        Array.apply(null, checkboxes).forEach((checkbox: HTMLElement) => {
            const mdcCheckbox = mdc.checkbox.MDCCheckbox.attachTo(checkbox);
            const formField = checkbox.parentElement;
            if (formField) {
                const mdcFormField = mdc.formField.MDCFormField.attachTo(formField);
                mdcFormField.input = mdcCheckbox;
            }
            if (checkbox.id) {
                this._mdcCheckboxes[checkbox.id] = mdcCheckbox;
            }
        });
    };

    private isDomainValid = (domain: string, cbSuccess: (dto: any) => void, cbError: (error: string) => void): void => {
        console.debug(`check ${domain}`);
        let url = UrlHelpers.getLinkRelHref('domainCheck');
        let req = new Request(url, {
            method: 'post',
            headers: {
                'accept': 'application/json'
            },
            body: JSON.stringify({
                domain: domain
            }),
            credentials: "same-origin"
        });
        fetch(req)
            .then((r: Response) => {
                if (r.status !== 200) {
                    throw `request failed with error code ${r.status}`;
                }
                return r.json();
            })
            .then((dtoJson) => {
                cbSuccess(dtoJson);
            })
            .catch((e: string) => {
                console.log(`Request to ${url} failed! Error: '${e}'`);
                if (cbError) {
                    cbError(e);
                }
            })
    };

    private createOrganization = (cbSuccess: (orgaId: string) => void, cbError: (error: string) => void): void => {
        console.log("create organization")
        const url = UrlHelpers.getLinkRelHref('organizations');
        const orgaName = this._wizardState.organizationName ? this._wizardState.organizationName : this._i18n.getTranslation('default_organisation_name_my_systems');
        const request = new Request(url, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
            },
            credentials: 'same-origin',
            body: JSON.stringify({
                name: orgaName
            })
        })
        fetch(request)
            .then(r => {
                if (r.status !== 201) {
                    throw `request failed with error code ${r.status}`;
                }
                return r.json();
            })
            .then(json => {
                let orgaId = json.replace(/"/g, '');
                if (!orgaId) {
                    throw "no orga id found";
                }
                if (cbSuccess) {
                    cbSuccess(orgaId);
                }
            })
            .catch(e => {
                console.log(`Request to ${url} failed! Error: '${e}'`);
                if (cbError) {
                    cbError(e);
                }
            });
    };

    private createTenantForOrganization = (cbSuccess: (tenantId: string) => void, cbError: (error: string) => void): void => {
        console.log("create tenant in organization");
        if (!this._wizardState.organization) {
            console.error('organization id missing');
            return;
        }
        const url = `${UrlHelpers.getLinkRelHref('organizations')}/${this._wizardState.organization}`;
        const request = new Request(url, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
            },
            credentials: 'same-origin'
        })
        this.retryableFetch(request, 3)
            .then(json => {
                let tenantId = json.replace(/"/g, '');
                if (!tenantId) {
                    throw "no tenant id found";
                }
                if (cbSuccess) {
                    cbSuccess(tenantId);
                }
            })
            .catch(e => {
                console.log(`Request to ${url} failed! Error: '${e}'`);
                if (cbError) {
                    cbError(e);
                }
            });
    };

    private showLongerExecutionMessage = (): void => {
        const el = <HTMLDivElement>document.getElementById('subscriptionWizardV2Final_progressbar_message');
        el.classList.remove('hide-element');
    }

    private hideLongerExecutionMessage = (): void => {
        const el = <HTMLDivElement>document.getElementById('subscriptionWizardV2Final_progressbar_message');
        el.classList.add('hide-element');
    }

    private updateTenant = (cbSuccess: () => void, cbError: (error: string) => void): void => {
        console.log("update tenant");
        if (!this._wizardState.tenant) {
            console.error('tenant id missing');
            return;
        }
        if (!this._wizardState.organization) {
            console.error('organization id missing');
            return;
        }
        const url = `${UrlHelpers.getLinkRelHref('tenants')}/${this._wizardState.tenant}`;

        const request = new Request(url, {
            method: 'PUT',
            headers: {
                'Accept': 'application/json',
            },
            credentials: 'same-origin',
            body: JSON.stringify({
                tenant: {
                    id: this._wizardState.tenant,
                    organizationId: this._wizardState.organization,
                    domainName: this._wizardState.domain
                },
                arguments: {
                    "dvcc:firstname": this._wizardState.customerInfo.firstname,
                    "dvcc:lastname": this._wizardState.customerInfo.lastname
                }
            })
        })
        fetch(request)
            .then(r => {
                if (r.status !== 200) {
                    console.debug('Response body:', r.body);
                    throw `request failed with error code ${r.status}`;
                }
                if (cbSuccess) {
                    cbSuccess();
                }
            })
            .catch(e => {
                console.log(`Request to ${url} failed! Error: '${e}'`);
                if (cbError) {
                    cbError(e);
                }
            });
    };

    private subscribe = (cbSuccess: () => void, cbError: (error: string) => void): void => {
        if (this._orderModeActive) {
            this.subscribeApps(this._wizardState.orderedApps ,cbSuccess, cbError);
            return;
        }
        this.subscribeApp(this._wizardState.app, cbSuccess, cbError);
    }

    private subscribeApps = (apps: string[], cbSuccess: () => void, cbError: (error: string) => void): void => {
        let remaining = [...apps];
        const app = remaining.pop();
        const cb = (): void => {
            if (remaining.length > 0) {
                this.subscribeApps(remaining, cbSuccess, cbError);
                return;
            }
            cbSuccess();
        }
        this.subscribeApp(app, cb, cbError);
    }

    private subscribeApp = (appName: string, cbSuccess: () => void, cbError: (error: string) => void): void => {
        if (!appName) {
            console.error('app name missing');
            return;
        }
        console.log(`subscribe app '${appName}'`);
        if (!this._wizardState.tenant) {
            console.error('tenant id missing');
            return;
        }
        if (this._createNewTenant && (!this._wizardState.customerInfo.firstname || !this._wizardState.customerInfo.lastname || !this._wizardState.customerInfo.company)) {
            console.error('required field on customer info missing');
            return;
        }
        const url = UrlHelpers.getLinkRelHref('subscription');
        let data = {
            action: "subscribe",
            tenantId: this._wizardState.tenant,
            appName: appName,
            customerInfo: this._createNewTenant ? this.customerInfoDtoFromState(this._wizardState) : null,
            subscribeMarketingCommunication: this.isMarketingSubscriptionChecked()
        }
        const request = new Request(url, {
            method: 'PUT',
            headers: {
                'Accept': 'application/json',
                // @ts-ignore
                'Accept-Language': navigator.language || navigator.userLanguage
            },
            credentials: 'same-origin',
            body: JSON.stringify(data)
        })
        fetch(request)
            .then(r => {
                if (r.status === 409) {
                    cbError('app dependency conflict');
                    return
                }
                if (r.status !== 200) {
                    throw `request failed with error code ${r.status}`;
                }
                if (cbSuccess) {
                    cbSuccess();
                }
            })
            .catch(e => {
                console.log(`Request to ${url} failed! Error: '${e}'`);
                if (cbError) {
                    cbError(e);
                }
            });
    };

    //mark: createSystem
    private setupCreateSystem = (): void => {
        const domainInputHtml = <HTMLInputElement>document.getElementById('subscriptionWizardV2CreateSystem_domain_input');
        if (!domainInputHtml) {
            console.error('domain input missing');
            return;
        }
        domainInputHtml.addEventListener('input', this.delayedCreateSystemValidation);

        const newOrganizationInput = <HTMLInputElement>document.getElementById('subscriptionWizardV2CreateSystem_newOrganization_input');
        if (!newOrganizationInput) {
            console.error('new organization input missing');
            return;
        }
        newOrganizationInput.addEventListener('input', this.delayedCreateSystemValidation);

        const btnCreate = <HTMLButtonElement>document.getElementById('subscriptionWizardV2CreateSystem_create');
        if (!btnCreate) {
            console.error("accept button for page 'createSystem' not found");
            return;
        }
        btnCreate.addEventListener('click', this.acceptCreateSystem);

        const btnBack = <HTMLButtonElement>document.getElementById('subscriptionWizardV2CreateSystem_back');
        if (!btnBack) {
            console.error("back button for page 'createSystem' not found");
            return;
        }
        btnBack.addEventListener('click', () => {
            this.goToPreviousPage();
        });

        const radioNewOrga = <HTMLInputElement>document.getElementById('subscriptionWizardV2CreateSystem_createNewOrganization');
        if (!radioNewOrga) {
            console.error('new organization button is missing');
        }
        radioNewOrga.addEventListener('change', this.displayNewOrganizationGroup);

        const radioSelectOrga = <HTMLInputElement>document.getElementById('subscriptionWizardV2CreateSystem_chooseExistingOrganization');
        if (!radioSelectOrga) {
            console.error('new organization button is missing');
            return;
        }
        radioSelectOrga.addEventListener('change', this.displaySelectOrganizationGroup);

        this.setupCreateSystemOrganizationDropdownSearch();
    };

    private setupCreateSystemOrganizationDropdownSearch = (): void => {
        const list = document.getElementById('subscriptionWizardV2OrganizationDropdownSearch_menu_list');
        if (!list) {
            console.error('organization list missing');
            return;
        }

        list.parentElement.addEventListener('scroll', this.handleOrganizationMenuScroll);

        const attributeWizardData = 'data-wizarddata';
        const attributeSelectedOrganisation = 'data-selectedorganization';
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type == "attributes" && mutation.attributeName == attributeWizardData) {
                    this.renderCreateSystemOrganizationDropdownSearch();
                }
                if (mutation.type == "attributes" && mutation.attributeName == attributeSelectedOrganisation) {
                    this.delayedCreateSystemValidation();
                }
            });
        });
        observer.observe(list, {
            attributeFilter: [attributeWizardData, attributeSelectedOrganisation],
        });

        const inputId = 'subscriptionWizardV2OrganizationDropdownSearch_input';
        const inputHtml = <HTMLInputElement>document.getElementById(inputId);
        inputHtml.addEventListener('input', () => {
            clearTimeout(this._delayTimer);
            this._delayTimer = setTimeout(() => {
                this.filterCreateSystemList();
            }, 250);
        })

        if (this._withPreselectedOrganization) {
            list.dataset.selectedorganization = this._wizardState.organization;
            inputHtml.disabled = true;
        }

        const menu = this._mdcMenus['subscriptionWizardV2OrganizationDropdownSearch_menu'];
        if (!menu) {
            console.error('search menu list missing');
            return;
        }
        inputHtml.addEventListener('focusin', (ev) => {
            console.debug('focusin event listener');
            ev.preventDefault();
            if (!menu.open) {
                inputHtml.blur();
                menu.open = true;
                setTimeout(() => {
                    inputHtml.focus();
                }, 200);
            }
        });
        inputHtml.addEventListener('focusout', (ev) => {
            console.debug('focusout event listener');
            if (menu.open) {
                setTimeout(() => {
                    menu.open = false;
                }, 250);
            }
        })

        this.clearDropdownSearchHelperText();
        this.renderCreateSystemOrganizationDropdownSearch();
    };

    private renderCreateSystemOrganizationDropdownSearch = (): void => {
        const data = this.readCreateSystemDataFromElement();
        if (!data) {
            console.debug("renderCreateSystemOrganizationDropdownSearch: data missing");
            return;
        }
        this.clearDropdownSearchHelperText();
        if (data.organizations.length == 0) {
            console.debug('renderCreateSystemOrganizationDropdownSearch: no organizations found for rendering');
            this.setNoMatchesDropdownSearchHelperText();
        }
        const selectionListId = 'subscriptionWizardV2OrganizationDropdownSearch_menu_list';
        const ul = document.getElementById(selectionListId);
        if (!ul) {
            console.debug(`renderCreateSystemOrganizationSearchMenu: cannot find ul element with id ${selectionListId}`);
            return;
        }
        const collectionUl = document.createElement('ul');
        data.organizations.forEach((orga, index) => {
            const li = this.getListItemForCreateSystemOrganizationDropdownSearchMenu(orga);
            const selectedOrgaId = ul.dataset.selectedorganization;
            if (selectedOrgaId) {
                if (selectedOrgaId == li.dataset.value) {
                    li.classList.add('mdc-list-item--selected');
                    li.setAttribute('aria-selected', 'true');
                }
            } else {
                if (index === 0) {
                    li.classList.add('mdc-list-item--selected');
                    li.setAttribute('aria-selected', 'true');
                }
            }

            collectionUl.appendChild(li);
        });
        ul.innerHTML = collectionUl.innerHTML;
        this.resetDropdownSearchMenuList();
    };

    private getListItemForCreateSystemOrganizationDropdownSearchMenu = (orga: wizardOrga): HTMLLIElement => {
        const li = document.createElement('li');
        li.classList.add('mdc-list-item');
        li.dataset.value = orga.organizationId;
        li.dataset.organisationname = orga.organizationName;
        li.setAttribute('role', 'menuitem');
        li.innerHTML = `<span class="mdc-list-item__ripple"></span>
        <span class="mdc-list-item__text">${orga.organizationName}</span>`
        return li;
    };

    private resetDropdownSearchMenuList = (): void => {
        const id = 'subscriptionWizardV2OrganizationDropdownSearch_menu_list';
        const mdcList: mdc.list.MDCList = this._mdcLists[id];
        if (!mdcList) {
            console.log(`resetSelectSystemMdcList: cannot find mdcList with id ${id}`);
            return
        }

        mdcList.destroy();

        const newMdcList: mdc.list.MDCList = mdc.list.MDCList.attachTo(document.getElementById(id));
        newMdcList.listen('MDCList:action', this.handleDropdownSearchListItemClick);

        this._mdcLists[id] = newMdcList;
    };

    private handleDropdownSearchListItemClick = (ev): void => {
        const index = ev.detail.index;
        const mdcList: mdc.list.MDCList = this._mdcLists['subscriptionWizardV2OrganizationDropdownSearch_menu_list'];
        if (!mdcList) {
            console.error('handleDropdownSearchListItemClick: mdc list not found');
            return;
        }
        const listItem = mdcList.listElements[index];
        if (!listItem) {
            console.error('handleDropdownSearchListItemClick: list item not found');
            return;
        }
        const ul = mdcList.root || mdcList._root;
        if (!ul) {
            console.error('handleDropdownSearchListItemClick: ul not found');
            return;
        }
        ul.dataset.selectedorganization = listItem.dataset.value;
        this.setSelectedOrganizationText(listItem.dataset.organisationname);
    };

    private filterCreateSystemList = (): void => {
        const input = this._mdcTextFields['subscriptionWizardV2OrganizationDropdownSearch_input'];
        if (!input) {
            console.debug('filterCreateSystemList: input not found');
            return;
        }
        this.clearDropdownSearchData();
        this.loadOrganizationList(this.getOrganizationListUrl(input.value), this.mergeCreateSystemData,
            (error) => {
                console.error(`loading organization data failed ${error}`)
                this.showNotification(this._i18n.getTranslation('subscriptionWizardV2_loadWizardDataFailed_error_message'), true);
            });
    };

    private acceptCreateSystem = (): void => {
        const domainInput = this._mdcTextFields['subscriptionWizardV2CreateSystem_domain_input'];
        const state = <WizardState>{
            domain: domainInput.value
        }
        if (this._createNewOrganization) {
            const organizationInput = this._mdcTextFields['subscriptionWizardV2CreateSystem_newOrganization_input'];
            state.organizationName = organizationInput.value;
            state.organization = '';
        } else {
            const selectionListId = 'subscriptionWizardV2OrganizationDropdownSearch_menu_list';
            const ul = document.getElementById(selectionListId);
            if (!ul) {
                console.debug(`acceptCreateSystem: cannot find ul element with id ${selectionListId}`);
                return;
            }
            state.organization = ul.dataset.selectedorganization;
            state.organizationName = '';
        }
        this._createNewTenant = true;
        this.patchWizardState(state);
        this.goToPage(WizardPages.ShoppingCart);
    };

    private renderCreateSystem = () => {
        const domainInput = this._mdcTextFields['subscriptionWizardV2CreateSystem_domain_input'];
        if (!domainInput) {
            console.error('material instance of domain input missing');
        }

        domainInput.value = this._wizardState.domain;

        if (!this._wizardState.domain) {
            domainInput.value = this.getDomainSuggestion();
        }

        const btnBack = document.getElementById('subscriptionWizardV2CreateSystem_back');
        if (!btnBack) {
            console.error('back button for create system missing');
            return;
        }
        btnBack.classList.remove('element-hidden');

        const orgaInput = this._mdcTextFields['subscriptionWizardV2CreateSystem_newOrganization_input'];
        if (!orgaInput) {
            console.error('material instance of new organization input missing');
        }

        if (this._createNewOrganization && !this._withPreselectedOrganization) {
            if (this._isNewUser) {
                this.hideAllOrganizationElements();
            } else {
                this.showAllOrganizationElements();
            }
            if (!orgaInput.value) {
                orgaInput.value = this._i18n.getTranslation('default_organisation_name_my_systems');
            }
        } else if (this._withPreselectedOrganization) {
            this.hideAllOrganizationElements();
        } else {
            this.displaySelectOrganizationGroup();

        }

        if (this._isOrganizationLimitExceeded) {
            orgaInput.disabled = true;
            orgaInput.value = '';
        }

        if (this._entrancePage == WizardPages.CreateSystem) {
            btnBack.classList.add('element-hidden');
        }
        this.renderOrganizationLimitInfo();
        this.renderCreateSystemButton();
    };

    private renderOrganizationLimitInfo = (): void => {
        const infoElementId = 'subscriptionWizardV2CreateSystem_newOrganization_limit_info';
        const limitInfo = <HTMLElement>document.getElementById(infoElementId);
        if (!limitInfo) {
            console.info(`element with id '${infoElementId}' not found`);
            return;
        }
        limitInfo.style.display = this._isOrganizationLimitExceeded && this._createNewOrganization ? '' : 'none';
    };

    private renderCreateSystemButton = (): void => {
        this.disableCreateSystemButton();
        const domainInput = this._mdcTextFields['subscriptionWizardV2CreateSystem_domain_input'];
        if (!domainInput) {
            console.error("material instance for create system input missing");
            return;
        }

        const organizationInput = this._mdcTextFields['subscriptionWizardV2CreateSystem_newOrganization_input'];
        if (!organizationInput) {
            console.error("material instance for new organization input missing");
            return;
        }

        if (this._createNewOrganization) {
            organizationInput.valid = true;
            organizationInput.helperTextContent = '';
            if (!organizationInput.value && !organizationInput.disabled) {
                organizationInput.valid = false;
                organizationInput.helperTextContent = this._i18n.getTranslation('subscriptionWizardV2_empty_input_message');
            } else if (organizationInput.disabled) {
                organizationInput.valid = false;
            }
        }

        const selectionListId = 'subscriptionWizardV2OrganizationDropdownSearch_menu_list';
        const ul = document.getElementById(selectionListId);
        if (!ul) {
            console.debug(`renderCreateSystemButton: cannot find ul element with id ${selectionListId}`);
            return;
        }

        domainInput.valid = true;
        domainInput.helperTextContent = "";
        if (domainInput.value) {
            if (domainInput.value.length < 3) {
                domainInput.valid = false;
                domainInput.helperTextContent = this._i18n.getTranslation('subscriptionWizardV2_domain_minlength_message');
                return;
            }
            this.isDomainValid(domainInput.value,
                (dto) => {
                    if (!dto.exists) {
                        if (dto.domain != domainInput.value) {
                            console.info(`checked value and actual input value are unequal: checked '${dto.domain}', actual '${domainInput.value}'`);
                            this.renderCreateSystemButton();
                            return;
                        }
                        if (this._createNewOrganization && (!organizationInput.valid || organizationInput.disabled)) {
                            return;
                        }
                        if (!this._createNewOrganization && ul.dataset.selectedorganization == '') {
                            return;
                        }
                        this.enableCreateSystemButton();
                        return;
                    }
                    let message = this._i18n.getTranslation('subscriptionWizardV2_domain_not_available_message');
                    if (dto.message === "base uri contains invalid characters") {
                        message = this._i18n.getTranslation('subscriptionWizardV2_domain_invalid_chars_message');
                    }
                    domainInput.valid = false;
                    domainInput.helperTextContent = message;
                },
                (error: string) => {
                    console.error("domain validity check failed:", error);
                    this.showNotification(this._i18n.getTranslation('subscriptionWizardV2_domainValidationFailed_error_message'));
                });
        }
    };

    private delayedCreateSystemValidation = (): void => {
        clearTimeout(this._delayTimer);
        this._delayTimer = setTimeout(() => {
            this.renderCreateSystemButton();
        }, 500)
    };

    private disableCreateSystemButton = (): void => {
        const btnCreate = <HTMLButtonElement>document.getElementById("subscriptionWizardV2CreateSystem_create");
        if (!btnCreate) {
            console.error("createSystemButton missing");
            return
        }
        btnCreate.disabled = true;
    };

    private enableCreateSystemButton = (): void => {
        const btnCreate = <HTMLButtonElement>document.getElementById("subscriptionWizardV2CreateSystem_create");
        if (!btnCreate) {
            console.error("createSystemButton missing");
            return
        }
        btnCreate.disabled = false;
    };

    private displayNewOrganizationGroup = (): void => {
        const group = <HTMLElement>document.getElementById('subscriptionWizardV2CreateSystem_newOrganization_element_group');
        if (!group) {
            console.error('newOrganization group missing');
            return;
        }
        group.style.display = '';
        this._createNewOrganization = true;
        this.hideSelectOrganizationGroup();
        this.setNewOrganizationRadioSelected();
        this.renderCreateSystemButton();
        this.renderOrganizationLimitInfo();
    };

    private hideNewOrganizationGroup = (): void => {
        const group = <HTMLElement>document.getElementById('subscriptionWizardV2CreateSystem_newOrganization_element_group');
        if (!group) {
            console.error('newOrganization group missing');
            return;
        }
        group.style.display = 'none';
    };

    private setNewOrganizationRadioSelected = (): void => {
        const radio = this._mdcRadios['subscriptionWizardV2CreateSystem_createNewOrganization'];
        if (!radio) {
            console.error('newOrganization radio missing');
            return;
        }
        radio.checked = true;
    };

    private displaySelectOrganizationGroup = (): void => {
        const group = <HTMLElement>document.getElementById('subscriptionWizardV2CreateSystem_selectOrganization_element_group');
        if (!group) {
            console.error('newOrganization group missing');
            return;
        }
        group.style.display = '';
        this._createNewOrganization = false;
        this.hideNewOrganizationGroup();
        this.setSelectOrganizationRadioSelected();
        this.renderCreateSystemButton();
        this.renderOrganizationLimitInfo();
    };

    private hideSelectOrganizationGroup = (): void => {
        const group = <HTMLElement>document.getElementById('subscriptionWizardV2CreateSystem_selectOrganization_element_group');
        if (!group) {
            console.error('newOrganization group missing');
            return;
        }
        group.style.display = 'none';
    };

    private setSelectOrganizationRadioSelected = (): void => {
        const radio = this._mdcRadios['subscriptionWizardV2CreateSystem_chooseExistingOrganization'];
        if (!radio) {
            console.error('selectOrganization radio missing');
            return;
        }
        radio.checked = true;
    };

    private hideAllOrganizationElements = (): void => {
        this.hideNewOrganizationGroup();
        this.hideSelectOrganizationGroup();
        this.hideOrganizationRadioButtonGroup();
    };

    private showAllOrganizationElements = (): void => {
        this.displayNewOrganizationGroup();
        this.displaySelectOrganizationGroup();
        this.displayOrganizationRadioButtonGroup();
    };

    private hideOrganizationRadioButtonGroup = (): void => {
        const group = <HTMLElement>document.getElementById('subscriptionWizardV2CreateSystem_radioButtonGroup');
        if (!group) {
            console.debug('organization radio group not found');
            return;
        }
        group.style.visibility = 'hidden';
    };

    private displayOrganizationRadioButtonGroup = (): void => {
        const group = <HTMLElement>document.getElementById('subscriptionWizardV2CreateSystem_radioButtonGroup');
        if (!group) {
            console.debug('organization radio group not found');
            return;
        }
        group.style.visibility = '';
    };

    private handleOrganizationMenuScroll = (): void => {
        console.debug('menu scroll handler');
        if (this._currentPage != WizardPages.CreateSystem) {
            return
        }
        const loadMoreData = () => {
            const ul = document.getElementById('subscriptionWizardV2OrganizationDropdownSearch_menu_list');
            if (!ul) {
                console.debug("handleOrganizationMenuScroll: ul list not found");
                return
            }
            const shouldLoadData = this.shouldLoadMoreCreateSystemData(ul.parentElement);
            if (!shouldLoadData) {
                return;
            }
            const data = this.readCreateSystemDataFromElement();
            if (!data) {
                console.log("handleOrganizationMenuScroll: cannot read data from element");
                return;
            }
            const more = data._links && ('more' in data._links) ? data._links["more"].href : null;
            if (!more) {
                console.debug('no "more" in links');
                return;
            }
            console.debug('loading more data');
            this.loadOrganizationList(more, this.mergeCreateSystemData,
                (error) => {
                    console.error(`loading organization data failed ${error}`)
                    this.showNotification(this._i18n.getTranslation('subscriptionWizardV2_loadWizardDataFailed_error_message'), true);
                });
        }
        clearTimeout(this._delayTimer);
        this._delayTimer = setTimeout(() => {
            loadMoreData();
        }, 100);
    };

    private shouldLoadMoreCreateSystemData = (el: HTMLElement): boolean => {
        const maxScrollPosition = el.scrollHeight - el.clientHeight;
        const diff = maxScrollPosition - el.scrollTop;
        const threshold = this.getCreateSystemScrollThreshold(el)
        return diff <= threshold;
    }

    private getCreateSystemScrollThreshold = (scrollElement: HTMLElement): number => {
        const liElements = Array.apply(null, scrollElement.getElementsByTagName('li'));
        if (liElements.length == 0) {
            console.debug('getCreateSystemScrollThreshold: no list elements found')
            return 0;
        }
        const heightMultiplier = 20;
        const lastElement = <HTMLLIElement>liElements[liElements.length - 1];
        return heightMultiplier * lastElement.offsetHeight;
    };

    private setSelectedOrganizationText = (organizationName: string): void => {
        const textField = this._mdcTextFields['subscriptionWizardV2OrganizationDropdownSearch_input'];
        if (!textField) {
            console.error('setSelectedOrganizationText: mdc text field not found');
            return;
        }
        textField.value = organizationName;
    };

    private setDropdownSearchHelperText = (content: string): void => {
        const textField = this._mdcTextFields['subscriptionWizardV2OrganizationDropdownSearch_input'];
        if (!textField) {
            console.error('setDropdownSearchHelperText: mdc text field not found');
            return;
        }
        textField.helperTextContent = content;
    };

    private setNoMatchesDropdownSearchHelperText = (): void => {
        const message = this._i18n.getTranslation('subscriptionWizardV2_createSystem_dropdownSearch_helperText_noMatches');
        this.setDropdownSearchHelperText(message);
    };

    private clearDropdownSearchHelperText = (): void => {
        this.setDropdownSearchHelperText('');
    };

    private clearDropdownSearchData = (): void => {
        const listId = 'subscriptionWizardV2OrganizationDropdownSearch_menu_list';
        const ul = document.getElementById(listId);
        if (!ul) {
            console.error(`cannot find element with id ${listId}`);
            return
        }
        ul.dataset.wizarddata = '';
    };

    //mark: selectSystem
    private setupSelectSystem = (): void => {
        const acceptBtn = <HTMLButtonElement>document.getElementById('subscriptionWizardV2SelectSystem_accept');
        if (!acceptBtn) {
            console.error('accept button missing');
            return;
        }
        acceptBtn.addEventListener('click', this.acceptSelectSystem);

        const btnBack = <HTMLButtonElement>document.getElementById('subscriptionWizardV2SelectSystem_back');
        if (!btnBack) {
            console.error("back button for page 'selectSystem' not found");
            return;
        }
        btnBack.addEventListener('click', this.goToPreviousPage);

        const btnNewSystem = <HTMLButtonElement>document.getElementById('subscriptionWizardV2SelectSystem_createNewSystem');
        if (btnNewSystem) {
            btnNewSystem.addEventListener('click', this.goToCreateSystem)
        } else {
            console.info("new system button for page 'selectSystem' not found");
        }

        const searchInput = <HTMLInputElement>document.getElementById('subscriptionWizardV2SelectSystem_search_input');
        if (!searchInput) {
            console.error('search input missing');
            return;
        }
        searchInput.addEventListener('input', ev => {
            clearTimeout(this._delayTimer);
            this._delayTimer = setTimeout(() => {
                this.filterSelectSystemList();
            }, 250);
        });

        this.setupSelectSystemList();
    };

    private setupSelectSystemList = (): void => {
        let listId = 'subscriptionWizardV2SelectSystem_orga_selection_list';
        const list = this._mdcLists[listId];
        if (!list) {
            console.error('organization list missing');
            return;
        }

        //add event listener for attribute changes on element
        const observedAttributeName = 'data-wizarddata';
        const uList = list.root;
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type == "attributes" && mutation.attributeName == observedAttributeName) {
                    this.renderSelectSystemList();
                }
            });
        });
        observer.observe(uList, {
            attributeFilter: [observedAttributeName],
        });

        this.resetSelectSystemMdcList(listId)
        this.renderSelectSystemList();
    };

    private handleSelectSystemListItemClick = (event) => {
        const index = event.detail.index
        const uList = <HTMLElement>event.target;

        const list = this._mdcLists['subscriptionWizardV2SelectSystem_orga_selection_list'];
        const li = list.listElements[index];

        uList.dataset.selectedorganization = li.parentElement.dataset.orgaid;
        uList.dataset.selectedtenant = li.dataset.tenantid;
        uList.dataset.selectedtenantdomain = li.dataset.tenantdomain;
        this.renderSelectSystemAccept();
    }

    private acceptSelectSystem = (): void => {
        const uList = <HTMLElement>document.getElementById('subscriptionWizardV2SelectSystem_orga_selection_list');
        if (!uList) {
            console.error('list group missing');
            return;
        }
        this.patchWizardState(<WizardState>{
            organization: uList.dataset.selectedorganization,
            organizationName: '',
            tenant: uList.dataset.selectedtenant,
            domain: uList.dataset.selectedtenantdomain
        });
        this._createNewTenant = false;
        this._createNewOrganization = false;
        this.goToPage(WizardPages.ShoppingCart);
    };

    private goToCreateSystem = (): void => {
        this.patchWizardState(<WizardState>{
            organization: '',
            organizationName: '',
            tenant: '',
            domain: ''
        });
        this.goToPage(WizardPages.CustomerInfo);
    };

    private openSelectSystemListProgressbar = (): void => {
        const id = 'loadListProgress';
        const progressBar = this._mdcProgressBars[id];
        progressBar.open();
    };

    private openCreateSystemListProgressbar = (): void => {
        const id = 'subscriptionWizardV2OrganizationDropdownSearch_menu_progress';
        const progressBar = this._mdcProgressBars[id];
        if (!progressBar) {
            console.error('openCreateSystemListProgressbar: progress bar not found');
            return;
        }
        progressBar.open();
    };

    private closeSelectSystemListProgressbar = (): void => {
        const id = 'loadListProgress';
        const progressBar = this._mdcProgressBars[id];
        progressBar.close();
    };

    private closeCreateSystemListProgressbar = (): void => {
        const id = 'subscriptionWizardV2OrganizationDropdownSearch_menu_progress';
        const progressBar = this._mdcProgressBars[id];
        if (!progressBar) {
            console.error('closeCreateSystemListProgressbar: progress bar not found');
            return;
        }
        progressBar.close();
    };

    private filterSelectSystemList = (): void => {
        const input = this._mdcTextFields['subscriptionWizardV2SelectSystem_search_input'];
        // clear ul wizard data
        this.clearWizardDataInElement();
        this.loadTenantList(this.getWizardDataUrl(input.value), this.mergeSelectSystemData, (error) => {
            console.error(`loading wizard data failed ${error}`)
            this.showNotification(this._i18n.getTranslation('subscriptionWizardV2_loadWizardDataFailed_error_message'), true);
        });
    };

    private handleSelectSystemLoadMoreButton = (e: Event): void => {
        const ul = document.getElementById('subscriptionWizardV2SelectSystem_orga_selection_list');
        if (!ul) {
            console.debug("handleSelectSystemLoadMoreButton: ul list not found");
            return
        }
        const data = this.readSelectSystemDataFromElement();
        if (!data) {
            console.log("handleSelectSystemLoadMoreButton: cannot read data from element");
            return;
        }
        const more = !!data._links && !!data._links["more"] ? data._links["more"].href : null;
        if (!more) {
            return;
        }
        this.loadTenantList(more, this.mergeSelectSystemData, (error) => {
            console.error(`handleSelectSystemLoadMoreButton: loading wizard data failed ${error}`)
            this.showNotification(this._i18n.getTranslation('subscriptionWizardV2_loadWizardDataFailed_error_message'), true);
        });
    };

    private renderSelectSystem = (): void => {
        const uList = <HTMLElement>document.getElementById('subscriptionWizardV2SelectSystem_orga_selection_list');
        if (!uList) {
            console.error('organization html list missing');
            return;
        }
        uList.dataset.selectedorganization = this._wizardState.organization;
        uList.dataset.selectedtenant = this._wizardState.tenant;
        const list = this._mdcLists['subscriptionWizardV2SelectSystem_orga_selection_list'];
        if (!list) {
            console.error('organization mdc list missing');
            return;
        }
        const listItems = list.listElements;
        for (let i = 0; i < listItems.length; i++) {
            const li = listItems[i];
            if (li.dataset.tenantid == uList.dataset.selectedtenant) {
                list.selectedIndex = i;
            }
        }
        const btnBack = <HTMLButtonElement>document.getElementById('subscriptionWizardV2SelectSystem_back');
        if (btnBack && this._entrancePage == WizardPages.SelectSystem) {
            btnBack.classList.add('element-hidden');
        }
        this.renderSelectSystemList();
        this.renderSelectSystemAccept();
        //todo: render create new system button based on limit
    };

    private renderSelectSystemAccept = (): void => {
        const btn = <HTMLButtonElement>document.getElementById('subscriptionWizardV2SelectSystem_accept');
        if (!btn) {
            console.error('accept button in select system missing');
        }
        const list = this._mdcLists['subscriptionWizardV2SelectSystem_orga_selection_list'];
        if (!list) {
            console.error('organization list missing');
            return;
        }
        const listroot = list.root;
        btn.disabled = listroot.dataset.selectedorganization === '' || listroot.dataset.selectedtenant === '' || listroot.dataset.selectedtenantdomain === ''

        // btn.disabled = list.selectedIndex < 0;
    };

    private renderSelectSystemList = () => {
        const data = this.readSelectSystemDataFromElement();
        if (!data) {
            console.log("renderSelectSystemList: data missing");
            return;
        }
        const selectionListId = 'subscriptionWizardV2SelectSystem_orga_selection_list';

        const ul = document.getElementById(selectionListId);
        if (!ul) {
            console.log(`renderSelectSystemList: cannot find ul element with id ${selectionListId}`);
            return;
        }

        const collectionList = document.createElement('ul');

        data.organizations.forEach((organization: wizardOrga) => {
            let orgaDiv = this.getSelectSystemListItemGroup(organization);

            if (organization.tenants) {
                organization.tenants.forEach((tenant: wizardTenant, index: number) => {
                    const tenantLi = this.getSelectSystemListItem(tenant);
                    orgaDiv.appendChild(tenantLi);

                    if (index == organization.tenants.length - 1) {
                        orgaDiv.insertAdjacentHTML('beforeend', `<li role="separator" class="mdc-list-divider"></li>`);
                    }
                });
            }
            collectionList.appendChild(orgaDiv);
        });

        ul.innerHTML = collectionList.innerHTML;

        ul.dataset.selectedorganization = '';
        ul.dataset.selectedtenant = '';
        ul.dataset.selectedtenantdomain = '';

        this.resetSelectSystemMdcList(selectionListId);

        this.renderSelectSystemListItemCountInfoText();

        this.renderSelectSystemListActions();

        this.renderSelectSystemAccept();
    };

    private renderSelectSystemListActions = () => {
        const hiddenClass = 'element-hidden';
        const btnId = 'subscriptionWizardV2SelectSystem_orga_selection_list_btn_more';
        const btn = <HTMLButtonElement>document.getElementById(btnId);
        const parentDiv = <HTMLDivElement>btn.parentElement;
        if (this.hasSelectSystemListMoreDataToLoad()) {
            parentDiv.classList.remove(hiddenClass);
            return
        }
        parentDiv.classList.add(hiddenClass);
    };

    private hasSelectSystemListMoreDataToLoad = (): boolean => {
        const data = this.readSelectSystemDataFromElement();
        if (!data) {
            console.log("handleSelectSystemLoadMoreButton: cannot read data from element");
            return false;
        }
        const more = !!data._links && !!data._links["more"] ? data._links["more"].href : null;
        if (!more) {
            return false;
        }
        return true;
    };

    private resetSelectSystemMdcList = (id: string) => {
        const mdcList: mdc.list.MDCList = this._mdcLists[id];
        if (!mdcList) {
            console.log(`resetSelectSystemMdcList: cannot find mdcList with id ${id}`);
            return
        }

        mdcList.destroy();

        const newMdcList: mdc.list.MDCList = mdc.list.MDCList.attachTo(document.getElementById(id));
        newMdcList.listen('MDCList:action', this.handleSelectSystemListItemClick);

        this._mdcLists[id] = newMdcList;
    }

    private getSelectSystemListItemGroup = (organization: wizardOrga): HTMLDivElement => {
        const orgaDiv = document.createElement('div');
        orgaDiv.classList.add('organization-list-item-group');
        orgaDiv.setAttribute('data-orgaid', organization.organizationId);

        const subheader = document.createElement('h3');
        subheader.classList.add('mdc-list-group__subheader');
        subheader.innerText = organization.organizationName;
        orgaDiv.appendChild(subheader);
        return orgaDiv
    };

    private getSelectSystemListItem = (tenant: wizardTenant): HTMLLIElement => {
        const li = document.createElement('li');
        li.classList.add('mdc-list-item', 'subscriptionWizardV2__selectSystemList__listItem');

        if (tenant.tenantId === this._wizardState.tenant) {
            li.classList.add('mdc-list-item--selected');
            li.setAttribute('aria-selected', 'true');
        }

        const tenantIsTerminated: boolean = !(tenant.terminationState == TenantTerminationState.notTerminated);
        if (tenantIsTerminated || tenant.isAppAlreadySubscribed) {
            li.classList.add('mdc-list-item--disabled');
        }

        li.setAttribute('tabindex', '-1');
        li.setAttribute('data-tenantid', tenant.tenantId);
        li.setAttribute('data-tenantdomain', tenant.tenantDomain);

        const prefixedDomainSuffix = document.getElementById('prefixedDomainSuffix').innerText;

        li.insertAdjacentHTML('beforeend',
            `<span class="mdc-list-item__ripple"></span>
             <span class="mdc-list-item__text">
                 <span class="mdc-list-item__primary-text">${Escaping.escapeHtml(tenant.tenantDisplayName)}</span>
                 <span class="mdc-list-item__secondary-text">https://${tenant.tenantDomain}${prefixedDomainSuffix}</span>
             </span>`
        );

        const stateDiv = document.createElement('div');
        stateDiv.classList.add('subscriptionWizardV2__selectSystemList__listItem-subscription-state');
        if (tenantIsTerminated) {
            stateDiv.innerText = this._i18n.getTranslation('order_wizard_section_TerminatedTenant_subscribed_label');
        }
        if (tenant.isAppAlreadySubscribed) {
            stateDiv.innerText = this._i18n.getTranslation('order_wizard_section_existingTenant_app_already_subscribed_label');
        }

        li.appendChild(stateDiv);
        return li;
    };

    private getSelectSystemListItemCount = (): number => {
        const selectionListId = 'subscriptionWizardV2SelectSystem_orga_selection_list';
        const ul = document.getElementById(selectionListId);
        if (!ul) {
            console.log(`cannot find ul element with id ${selectionListId}`);
            return 0;
        }
        return ul.getElementsByClassName('subscriptionWizardV2__selectSystemList__listItem').length;
    };

    private renderSelectSystemListItemCountInfoText = (): void => {
        const elementId = 'subscriptionWizardV2SelectSystem_list_counter'
        const p = <HTMLParagraphElement>document.getElementById(elementId);
        if (!p) {
            console.log(`cannot find p element with id ${elementId}`);
            return;
        }
        const itemCount = this.getSelectSystemListItemCount();
        const wizardData = this.readSelectSystemDataFromElement();
        const i18nKey = wizardData._links && wizardData._links["more"] ? 'subscriptionWizardV2_selectSystem_listCount_infoText_with_more' : 'subscriptionWizardV2_selectSystem_listCount_infoText';
        p.innerText = i18n.replaceTranslationPlaceholder(this._i18n.getTranslation(i18nKey), "{COUNTER}", itemCount.toString());
    };

    //mark: customerInfo
    private setupCustomerInfo = (): void => {
        const customerInfo = this._wizardPageElements['subscriptionWizardV2CustomerInfo'];
        if (!customerInfo) {
            console.info("customer info not found");
            return;
        }
        const btnAccept = <HTMLButtonElement>document.getElementById('subscriptionWizardV2CustomerInfo_accept');

        btnAccept.addEventListener('click', this.acceptCustomerInfo);

        const btnBack = <HTMLButtonElement>document.getElementById('subscriptionWizardV2CustomerInfo_back');
        btnBack.addEventListener('click', () => {
            this.goToPreviousPage();
        });

        const mandatoryFields: HTMLInputElement[] = [
            <HTMLInputElement>document.getElementById('subscriptionWizardV2CustomerInfo_firstname_input'),
            <HTMLInputElement>document.getElementById('subscriptionWizardV2CustomerInfo_lastname_input'),
            <HTMLInputElement>document.getElementById('subscriptionWizardV2CustomerInfo_phone_input'),
            <HTMLInputElement>document.getElementById('subscriptionWizardV2CustomerInfo_country_input'),
            <HTMLInputElement>document.getElementById('subscriptionWizardV2CustomerInfo_company_input')
        ];
        mandatoryFields.forEach((field: HTMLInputElement) => {
            field.addEventListener('input', () => {
                clearTimeout(this._delayTimer);
                this._delayTimer = setTimeout(() => {
                    this.renderCustomerInfoButton();
                }, 250);
            })
        });

        const infoSelect = this._mdcSelects['subscriptionWizardV2CustomerInfo_employeesCount_select'];
        if (!infoSelect) {
            console.info("employees count select for customer info not found");
            return;
        }
        //workaround for: selectedIndex updates via api on not visible selects will update the index, but not the layout of the select
        const observer = new MutationObserver(() => {
            if (customerInfo.classList.contains("subscriptionWizardV2__step--current")) {
                this.renderCustomerInfoEmployeesCount();
            }
        });
        observer.observe(customerInfo, {attributes: true});
    };

    private acceptCustomerInfo = (): void => {
        this.patchWizardState(<WizardState>{
            customerInfo: {
                firstname: this._mdcTextFields['subscriptionWizardV2CustomerInfo_firstname_input'].value,
                lastname: this._mdcTextFields['subscriptionWizardV2CustomerInfo_lastname_input'].value,
                phone: this._mdcTextFields['subscriptionWizardV2CustomerInfo_phone_input'].value,
                country: this._mdcTextFields['subscriptionWizardV2CustomerInfo_country_input'].value,
                company: this._mdcTextFields['subscriptionWizardV2CustomerInfo_company_input'].value,
                employeesCount: this.employeesCountValueToKey(this._mdcSelects['subscriptionWizardV2CustomerInfo_employeesCount_select'].value)
            }
        });
        this.goToPage(WizardPages.CreateSystem);
    };

    private renderCustomerInfo = (): void => {
        const customerInfoState = this._wizardState.customerInfo;
        this._mdcTextFields['subscriptionWizardV2CustomerInfo_firstname_input'].value = customerInfoState.firstname;
        this._mdcTextFields['subscriptionWizardV2CustomerInfo_lastname_input'].value = customerInfoState.lastname;
        this._mdcTextFields['subscriptionWizardV2CustomerInfo_phone_input'].value = customerInfoState.phone;
        this._mdcTextFields['subscriptionWizardV2CustomerInfo_country_input'].value = customerInfoState.country;
        this._mdcTextFields['subscriptionWizardV2CustomerInfo_company_input'].value = customerInfoState.company;

        const btnBack = <HTMLButtonElement>document.getElementById('subscriptionWizardV2CustomerInfo_back');
        if (btnBack && this._entrancePage == WizardPages.CustomerInfo) {
            btnBack.classList.add('element-hidden');
        }

        this.renderCustomerInfoEmployeesCount();
        this.renderCustomerInfoButton();
    };

    private renderCustomerInfoEmployeesCount = (): void => {
        const select = this._mdcSelects['subscriptionWizardV2CustomerInfo_employeesCount_select'];
        select.selectedIndex = this.employeesCountKeyToIndex(this._wizardState.customerInfo.employeesCount);
    };

    private renderCustomerInfoButton = (): void => {
        const btnAccept = <HTMLButtonElement>document.getElementById('subscriptionWizardV2CustomerInfo_accept');
        btnAccept.disabled = true;

        const mandatoryFields: mdc.textField.MDCTextField[] = this.getRequiredTextFieldsForParent('subscriptionWizardV2CustomerInfo');

        let disable = false;
        mandatoryFields.forEach((field: mdc.textField.MDCTextField) => {
            if (!field.valid || !field.value.trim()) {
                disable = true;
            }
        });
        btnAccept.disabled = disable;
    };

    //mark: shoppingCart
    private setupShoppingCart = (): void => {
        const btnAccept = <HTMLButtonElement>document.getElementById('subscriptionWizardV2ShoppingCart_accept');
        if (!btnAccept) {
            console.error("accept button for page 'shoppingCart' not found");
            return;
        }
        btnAccept.addEventListener('click', this.acceptShoppingCart);
        const btnBack = <HTMLButtonElement>document.getElementById('subscriptionWizardV2ShoppingCart_back');
        if (!btnBack) {
            console.error("back button for page 'shoppingCart' not found");
            return;
        }
        btnBack.addEventListener('click', () => {
            this.goToPreviousPage();
        })
        const btnChangeDomain = <HTMLButtonElement>document.getElementById('subscriptionWizardV2ShoppingCart_system_btn_change');
        if (btnChangeDomain) {
            btnChangeDomain.addEventListener('click', this.openChangeDomainDialog);
        }
        const btnChangeCustomerInfo = <HTMLButtonElement>document.getElementById('subscriptionWizardV2ShoppingCart_customerInfo_btn_change');
        if (btnChangeCustomerInfo) {
            btnChangeCustomerInfo.addEventListener('click', this.openChangeCustomerInfoDialog);
        }
        const appDocumentsGroup = <HTMLElement>document.getElementById('subscriptionWizardV2ShoppingCart_documents_group');
        if (appDocumentsGroup) {
            this.setupAppDocumentsGroup();
        }
        const basisAppDocumentsGroup = <HTMLElement>document.getElementById('subscriptionWizardV2ShoppingCart_basisdocuments_group');
        if (basisAppDocumentsGroup) {
            this.setupBasisAppDocumentsGroup();
        }
        const appPriceList = <HTMLElement>document.getElementById('subscriptionWizardV2ShoppingCart_app_priceList');
        if (appPriceList) {
            appPriceList.addEventListener('scroll', this.handlePriceListScroll);
            this.elementScrollToBottom(appPriceList);
            this.elementScrollToTop(appPriceList);
        }
        this.showSingleArticleMode();
        if (this._orderModeActive) {
            this.showOrderMode();
        }
    };

    private showSingleArticleMode = () => {
        const single = document.getElementById('subscriptionWizardV2ShoppingCart_articles');
        if (!single) {
            console.error("element 'subscriptionWizardV2ShoppingCart_articles' not found");
            return;
        }
        const order = document.getElementById('subscriptionWizardV2ShoppingCart_orderArticles');
        if (!order) {
            console.error("element 'subscriptionWizardV2ShoppingCart_orderArticles' not found");
            return;
        }
        single.classList.remove('hide-element');
        order.classList.add('hide-element');
    };

    private showOrderMode = () => {
        const single = document.getElementById('subscriptionWizardV2ShoppingCart_articles');
        if (!single) {
            console.error("element 'subscriptionWizardV2ShoppingCart_articles' not found");
            return;
        }
        const order = document.getElementById('subscriptionWizardV2ShoppingCart_orderArticles');
        if (!order) {
            console.error("element 'subscriptionWizardV2ShoppingCart_orderArticles' not found");
            return;
        }
        order.classList.remove('hide-element');
        single.classList.add('hide-element');
    };

    private handlePriceListScroll = (e: Event) => {
        const priceList = <HTMLElement>e.target;
        const shadowDiv = <HTMLElement>priceList.querySelector('.top-shadow-element');
        if (!shadowDiv) {
            console.debug('price list shadow element not found');
            return;
        }
        if (!this.elementScrolledTop(priceList)) {
            shadowDiv.classList.add('top-shadow-element__active');
        } else {
            shadowDiv.classList.remove('top-shadow-element__active');
        }
        this.renderPriceListScrollIndicators();
    }

    private setupAppDocumentsGroup = (): void => {
        const btnAppDocuments = <HTMLAnchorElement>document.getElementById('subscriptionWizardV2ShoppingCart_documents_group_btn_open');
        if (!btnAppDocuments) {
            console.error('app documents button not found');
            return;
        }
        const menu = this._mdcMenus['subscriptionWizardV2ShoppingCart_documents_group_menu'];
        if (!menu) {
            console.error('app document menu not found');
            return;
        }
        btnAppDocuments.addEventListener('click', (e: Event) => {
            e.preventDefault();
            menu.open = !menu.open;
            return false;
        });

        const listElements = menu.items;
        listElements.forEach((el: HTMLElement) => {
            el.addEventListener('click', () => {
                const url = el.dataset.doclink;
                window.open(url, '_blank');
            })
        })
    };

    private setupBasisAppDocumentsGroup = (): void => {
        const btnAppDocuments = <HTMLAnchorElement>document.getElementById('subscriptionWizardV2ShoppingCart_basisdocuments_group_btn_open');
        if (!btnAppDocuments) {
            console.debug('basis app documents button not found');
            return;
        }
        const menu = this._mdcMenus['subscriptionWizardV2ShoppingCart_basisdocuments_group_menu'];
        if (!menu) {
            console.debug('basis app documents button not found');
            return;
        }
        btnAppDocuments.addEventListener('click', (e: Event) => {
            e.preventDefault();
            menu.open = !menu.open;
            return false;
        });

        const listElements = menu.items;
        listElements.forEach((el: HTMLElement) => {
            el.addEventListener('click', () => {
                const url = el.dataset.doclink;
                window.open(url, '_blank');
            })
        })
    };

    private acceptShoppingCart = (): void => {
        this.dispatchGoogleTagManagerEvent();
        this.goToPage(WizardPages.Final);

        if (!this._wizardState.organization) {
            this.newOrganizationCheckout();
            return
        }
        if (!this._wizardState.tenant) {
            this.newTenantCheckout();
            return;
        }
        this.subscribeAppCheckout();
    };

    private newOrganizationCheckout = (): void => {
        setTimeout(() => {
            this.createOrganization((orgaId: string) => {
                this.patchWizardState(<WizardState>{organization: orgaId});
                this.createTenantForOrganization((tenantId: string) => {
                    this.patchWizardState(<WizardState>{tenant: tenantId});
                    this.updateTenant(() => {
                        this.subscribe(() => {
                            this.setSubscriptionSuccessful(true);
                        }, this.subscriptionError);
                    }, this.subscriptionError);
                }, this.subscriptionError);
            }, this.subscriptionError);
        }, 3000)
    };

    private newTenantCheckout = (): void => {
        setTimeout(() => {
            this.createTenantForOrganization((tenantId: string) => {
                this.patchWizardState(<WizardState>{tenant: tenantId});
                this.updateTenant(() => {
                    this.subscribe(() => {
                        this.setStorageForPreselectedOrganizationUpdateEvent();
                        this.setSubscriptionSuccessful(true);
                    }, this.subscriptionError);
                }, this.subscriptionError);
            }, this.subscriptionError);
        }, 3000);
    }

    private subscribeAppCheckout = (): void => {
        setTimeout(() => {
            this.subscribe(() => {
                this.setSubscriptionSuccessful(true);
            }, this.subscriptionError);
        }, 3000);
    };

    private subscriptionError = (error: string): void => {
        //todo: proper error handling
        this.hideLongerExecutionMessage();
        console.error(error)
        this.goToPreviousPage();
        console.error(`subscription failed ${error}`)
        switch (error) {
            case "app dependency conflict":
                this.showNotification(this._i18n.getTranslation('subscriptionWizardV2_subscriptionFailed_error_message_appOrDepNotApproved'), true);
                break;
            default:
                this.showNotification(this._i18n.getTranslation('subscriptionWizardV2_subscriptionFailed_error_message'), true);
        }
    };

    private renderShoppingCart = (): void => {
        const company = <HTMLParagraphElement>document.getElementById('subscriptionWizardV2ShoppingCart_contact_company');
        if (!company) {
            console.error("contact field 'company' missing");
        }
        const name = <HTMLParagraphElement>document.getElementById('subscriptionWizardV2ShoppingCart_contact_name');
        if (!name) {
            console.error("contact field 'name' missing");
        }
        const country = <HTMLParagraphElement>document.getElementById('subscriptionWizardV2ShoppingCart_contact_country');
        if (!country) {
            console.error("contact field 'country' missing");
        }
        const phone = <HTMLParagraphElement>document.getElementById('subscriptionWizardV2ShoppingCart_contact_phone');
        if (!phone) {
            console.error("contact field 'phone' missing");
        }
        company.innerText = this._wizardState.customerInfo.company;
        name.innerText = `${this._wizardState.customerInfo.firstname} ${this._wizardState.customerInfo.lastname}`;
        country.innerText = this._wizardState.customerInfo.country;
        phone.innerText = this._wizardState.customerInfo.phone;

        const domain = <HTMLParagraphElement>document.getElementById('subscriptionWizardV2ShoppingCart_system_domain');
        if (!domain) {
            console.error("system field 'domain' missing");
        }
        domain.innerText = this.getUrlFromDomain();
        const baseAppNote = <HTMLParagraphElement>document.getElementById('subscriptionWizardV2ShoppingCart_baseApp_note');
        if (!baseAppNote) {
            console.error("element base app note missing");
        }
        const changeSystemBtn = <HTMLButtonElement>document.getElementById('subscriptionWizardV2ShoppingCart_system_btn_change');
        if (!changeSystemBtn) {
            console.error('change system button missing');
        }
        const customerData = <HTMLDivElement>document.getElementById('subscriptionWizardV2ShoppingCart_customer_data');
        if (!customerData) {
            console.error('customer data field missing');
        }
        if (this._createNewTenant) {
            baseAppNote.classList.remove('element-hidden');
            changeSystemBtn.style.display = '';
            customerData.style.display = '';
        } else {
            baseAppNote.classList.add('element-hidden');
            changeSystemBtn.style.display = 'none';
            customerData.style.display = 'none';
        }

        this.renderPriceListScrollIndicators();
    };

    private renderPriceListScrollIndicators = (): void => {
        const priceList = <HTMLElement>document.getElementById('subscriptionWizardV2ShoppingCart_app_priceList');
        if (!priceList) {
            console.debug('price list element not found');
            return;
        }
        const topScrollIndicator = <HTMLElement>priceList.querySelector('.scroll-indicator.top');
        const bottomScrollIndicator = <HTMLElement>priceList.querySelector('.scroll-indicator.bottom');
        if (!topScrollIndicator || !bottomScrollIndicator) {
            console.error('scroll indicator missing');
            return;
        }

        let scrollable: boolean;
        this.executeElementCalculationForInvisibleCart(() => {
            scrollable = this.elementScrollable(priceList);
        });
        if (!scrollable) {
            topScrollIndicator.style.display = 'none';
            bottomScrollIndicator.style.display = 'none';
            return;
        }

        topScrollIndicator.style.display = '';
        bottomScrollIndicator.style.display = '';

        let scrolledTop: boolean;
        this.executeElementCalculationForInvisibleCart(() => {
            scrolledTop = this.elementScrolledTop(priceList);
        });
        if (scrolledTop) {
            topScrollIndicator.style.display = 'none';
        }
        let scrolledBottom: boolean;
        this.executeElementCalculationForInvisibleCart(() => {
            scrolledBottom = this.elementScrolledBottom(priceList);
        });
        if (scrolledBottom) {
            console.debug(`element is scrolled BOTTOM with scrollHeight = ${priceList.scrollHeight} and offsetHeight = ${priceList.offsetHeight}`);
            bottomScrollIndicator.style.display = 'none';
        }
    };

    private isMarketingSubscriptionChecked = (): boolean => {
        const marketingCheckbox = this._mdcCheckboxes['subscriptionWizardV2ShoppingCart_marketingCommunication_checkbox'];
        console.debug("marketingCheckbox: ", marketingCheckbox);
        if (marketingCheckbox) {
            console.debug(`marketingCheckbox value: ${marketingCheckbox.checked}`);
        }
        return marketingCheckbox ? marketingCheckbox.checked : false;
    };

    //workaround for elements with style "display: 'none'"
    //such elements have height and width set to 0, so size based calculations for rendering will fail
    private executeElementCalculationForInvisibleCart = (calculation: () => void): void => {
        const hiddenClass = 'subscriptionWizardV2__step--hidden';
        const shoppingCart = <HTMLElement>document.getElementById('subscriptionWizardV2ShoppingCart');
        if (!shoppingCart) {
            console.debug('shopping cart page not found');
            calculation();
            return;
        }
        if (!shoppingCart.classList.contains(hiddenClass)) {
            console.debug('shopping cart page is not hidden');
            calculation();
            return;
        }
        shoppingCart.classList.remove(hiddenClass);
        calculation()
        shoppingCart.classList.add(hiddenClass);
    };

    //mark: final
    private renderFinal = (): void => {
        const pBar = this._mdcProgressBars['subscriptionWizardV2Final_progressbar'];
        if (!pBar) {
            console.debug("progress bars:", this._mdcProgressBars);
            console.error("progress bar in final missing");
            return;
        }
        const btnFinal = <HTMLButtonElement>document.getElementById('subscriptionWizardV2Final_enter_cloud');
        if (!btnFinal) {
            console.error("button in final missing");
            return;
        }
        if (this._subscriptionSuccessful) {
            this.hideLongerExecutionMessage();
            pBar.close();
            btnFinal.disabled = false;
            btnFinal.addEventListener('click', () => {
                open(this.getGettingStartedUrl(), "_blank");
            });
            return;
        }
        pBar.open();
        btnFinal.disabled = true;
    };

    //mark: notification
    private showNotification = (message: string, disableCloseTimer: boolean = false): void => {
        if (!this._mdcSnackbars.hasOwnProperty('subscriptionWizardV2Notification')) {
            console.error('mdcSnackbar not found');
            return;
        }
        const defaultCloseTimeoutValue = 10000;
        const disableCloseTimeoutValue = -1;
        const mdcSnackbar = this._mdcSnackbars['subscriptionWizardV2Notification'];
        mdcSnackbar.labelText = message;
        mdcSnackbar.closeOnEscape = false;
        mdcSnackbar.timeoutMs = disableCloseTimer ? disableCloseTimeoutValue : defaultCloseTimeoutValue;
        mdcSnackbar.open();
    };

    //mark: changeDomain dialog
    private setupChangeDomainDialog(): void {
        const dialog = this._mdcDialogs['subscriptionWizardV2ChangeDomainDialog'];
        if (!dialog) {
            console.error('change domain dialog missing');
            return;
        }
        const input = this._mdcTextFields['subscriptionWizardV2ChangeDomainDialog_domain_input'];
        if (!input) {
            console.error('change domain input missing');
            return;
        }
        dialog.listen('MDCDialog:opening', () => {
            this.renderChangeDomainDialog();
        });
        dialog.listen('MDCDialog:closed', (e: mdc.dialog.MDCDialogCloseEventDetail) => {
            if (e.detail.action == "accept") {
                this.patchWizardState(<WizardState>{domain: input.value});
            }
        });
        const htmlInput = <HTMLInputElement>input.root.getElementsByTagName('input')[0];
        if (!htmlInput) {
            console.error('change domain html input missing');
            return;
        }
        htmlInput.addEventListener('input', () => {
            this.disableChangeDomainAcceptButton();
            clearTimeout(this._delayTimer);
            this._delayTimer = setTimeout(() => {
                this.renderChangeDomainAcceptButton();
            }, 500)
        });
    }

    private renderChangeDomainDialog = (): void => {
        const input = this._mdcTextFields['subscriptionWizardV2ChangeDomainDialog_domain_input'];
        if (!input) {
            console.error('change domain input missing');
            return;
        }
        input.value = this._wizardState.domain;
        this.renderChangeDomainAcceptButton();
    };

    private disableChangeDomainAcceptButton = (): void => {
        const btn = <HTMLButtonElement>document.getElementById('subscriptionWizardV2ChangeDomainDialog_btn_accept');
        if (!btn) {
            console.error('change domain accept button missing');
            return;
        }
        btn.disabled = true;
    };

    private enableChangeDomainAcceptButton = (): void => {
        const btn = <HTMLButtonElement>document.getElementById('subscriptionWizardV2ChangeDomainDialog_btn_accept');
        if (!btn) {
            console.error('change domain accept button missing');
            return;
        }
        btn.disabled = false;
    };

    private renderChangeDomainAcceptButton = (): void => {
        this.disableChangeDomainAcceptButton();
        const input = this._mdcTextFields['subscriptionWizardV2ChangeDomainDialog_domain_input'];
        if (!input) {
            console.error("material instance for create system input missing");
            return;
        }
        input.valid = true;
        input.helperTextContent = "";
        if (input.value) {
            if (input.value.length < 3) {
                input.valid = false;
                input.helperTextContent = this._i18n.getTranslation('subscriptionWizardV2_domain_minlength_message');
                return;
            }
            this.isDomainValid(input.value,
                (dto) => {
                    if (!dto.exists) {
                        if (dto.domain != input.value) {
                            console.info(`checked value and actual input value are unequal: checked '${dto.domain}', actual '${input.value}'`);
                            this.renderChangeDomainAcceptButton();
                            return;
                        }
                        this.enableChangeDomainAcceptButton();
                        return
                    }
                    let message = this._i18n.getTranslation('subscriptionWizardV2_domain_not_available_message');
                    if (dto.message === "base uri contains invalid characters") {
                        message = this._i18n.getTranslation('subscriptionWizardV2_domain_invalid_chars_message');
                    }
                    input.valid = false;
                    input.helperTextContent = message;
                },
                (error: string) => {
                    console.error("domain validity check failed:", error);
                    this.showNotification(this._i18n.getTranslation('subscriptionWizardV2_domainValidationFailed_error_message'));
                });
        }
    }

    private openChangeDomainDialog = (): void => {
        const dialog = this._mdcDialogs['subscriptionWizardV2ChangeDomainDialog'];
        if (!dialog) {
            console.error('change domain dialog missing');
            return;
        }
        dialog.open();
    };

    //mark: changeCustomerInfo dialog
    private setupChangeCustomerInfoDialog = (): void => {
        const dialog = this._mdcDialogs['subscriptionWizardV2ChangeCustomerInfoDialog'];
        if (!dialog) {
            console.error('change customer info dialog missing');
            return;
        }
        dialog.listen('MDCDialog:opening', () => {
            this.renderChangeCustomerInfoDialog();
        });
        dialog.listen('MDCDialog:opened', () => {
            this.renderChangeCustomerInfoDialogEmployeesCount();
        });
        dialog.listen('MDCDialog:closed', (e: mdc.dialog.MDCDialogCloseEventDetail) => {
            if (e.detail.action == "accept") {
                this.acceptChangeCustomerInfo();
            }
        });

        const mandatoryFields: HTMLInputElement[] = [
            <HTMLInputElement>document.getElementById('subscriptionWizardV2ChangeCustomerInfoDialog_firstname_input'),
            <HTMLInputElement>document.getElementById('subscriptionWizardV2ChangeCustomerInfoDialog_lastname_input'),
            <HTMLInputElement>document.getElementById('subscriptionWizardV2ChangeCustomerInfoDialog_phone_input'),
            <HTMLInputElement>document.getElementById('subscriptionWizardV2ChangeCustomerInfoDialog_country_input'),
            <HTMLInputElement>document.getElementById('subscriptionWizardV2ChangeCustomerInfoDialog_company_input')
        ];

        mandatoryFields.forEach((field: HTMLInputElement) => {
            field.addEventListener('input', () => {
                clearTimeout(this._delayTimer);
                this._delayTimer = setTimeout(() => {
                    this.renderChangeCustomerInfoDialogAcceptButton();
                }, 250);
            })
        });
    };

    private renderChangeCustomerInfoDialogAcceptButton = (): void => {
        const btnAccept = <HTMLButtonElement>document.getElementById('subscriptionWizardV2ChangeCustomerInfoDialog_btn_accept');
        btnAccept.disabled = true;
        const mandatoryFields: mdc.textField.MDCTextField[] = this.getRequiredTextFieldsForParent('subscriptionWizardV2ChangeCustomerInfoDialog');
        let disable = false;
        mandatoryFields.forEach((field: mdc.textField.MDCTextField) => {
            if (!field.valid || !field.value.trim()) {
                disable = true;
            }
        });
        btnAccept.disabled = disable;
    };

    private renderChangeCustomerInfoDialog = (): void => {
        const customerInfoState = this._wizardState.customerInfo;
        this._mdcTextFields['subscriptionWizardV2ChangeCustomerInfoDialog_firstname_input'].value = customerInfoState.firstname;
        this._mdcTextFields['subscriptionWizardV2ChangeCustomerInfoDialog_lastname_input'].value = customerInfoState.lastname;
        this._mdcTextFields['subscriptionWizardV2ChangeCustomerInfoDialog_phone_input'].value = customerInfoState.phone;
        this._mdcTextFields['subscriptionWizardV2ChangeCustomerInfoDialog_country_input'].value = customerInfoState.country;
        this._mdcTextFields['subscriptionWizardV2ChangeCustomerInfoDialog_company_input'].value = customerInfoState.company;

        this.renderChangeCustomerInfoDialogEmployeesCount();
        this.renderChangeCustomerInfoDialogAcceptButton();
    };

    private renderChangeCustomerInfoDialogEmployeesCount = (): void => {
        const select = this._mdcSelects['subscriptionWizardV2ChangeCustomerInfoDialog_employeesCount_select'];
        select.selectedIndex = this.employeesCountKeyToIndex(this._wizardState.customerInfo.employeesCount);
    }

    private acceptChangeCustomerInfo = (): void => {
        this.patchWizardState(<WizardState>{
            customerInfo: {
                firstname: this._mdcTextFields['subscriptionWizardV2ChangeCustomerInfoDialog_firstname_input'].value,
                lastname: this._mdcTextFields['subscriptionWizardV2ChangeCustomerInfoDialog_lastname_input'].value,
                phone: this._mdcTextFields['subscriptionWizardV2ChangeCustomerInfoDialog_phone_input'].value,
                country: this._mdcTextFields['subscriptionWizardV2ChangeCustomerInfoDialog_country_input'].value,
                company: this._mdcTextFields['subscriptionWizardV2ChangeCustomerInfoDialog_company_input'].value,
                employeesCount: this.employeesCountValueToKey(this._mdcSelects['subscriptionWizardV2ChangeCustomerInfoDialog_employeesCount_select'].value)
            }
        });
    };

    private openChangeCustomerInfoDialog = (): void => {
        const dialog = this._mdcDialogs['subscriptionWizardV2ChangeCustomerInfoDialog'];
        if (!dialog) {
            console.error('change domain dialog missing');
            return;
        }
        dialog.open();
    };

    //mark: helper
    private getEmptyState(): WizardState {
        return {
            domain: "",
            organization: "",
            organizationName: "",
            tenant: "",
            app: "",
            order: "",
            orderedApps: [],
            customerInfo: {
                firstname: "",
                lastname: "",
                phone: "",
                country: "",
                company: "",
                employeesCount: EmployeesCount.None
            }
        } as WizardState
    }

    private getUrlFromDomain = (): string => {
        const prefixedDomainSuffix = document.getElementById('prefixedDomainSuffix').innerText;
        return this._wizardState.domain ? `https://${this._wizardState.domain}${prefixedDomainSuffix}` : "MISSING";
    };

    private getGettingStartedUrl = (): string => {
        const gettingStarted = UrlHelpers.getMetaData('gettingStarted');
        return `${this.getUrlFromDomain()}${gettingStarted}`
    };

    private getWizardDataUrl = (search?: string): string => {
        const linkSelf = UrlHelpers.getLinkRelHref('self');
        let url = `${linkSelf}?app=${this._wizardState.app}`;
        if (search) {
            url = `${url}&search=${search}`
        }
        return url
    };

    private getOrganizationListUrl = (search?: string): string => {
        let uri = new URL(UrlHelpers.getLinkRelHref('organizations'));
        uri.searchParams.set('withpaging', '1');
        if (search) {
            uri.searchParams.set('search', search);
        }
        return uri.toString();
    };

    private readSelectSystemDataFromElement = (): wizardData => {
        const selectionListId = 'subscriptionWizardV2SelectSystem_orga_selection_list';
        return this.readDataFromElement(selectionListId);
    };

    private readCreateSystemDataFromElement = (): wizardData => {
        const menuListId = 'subscriptionWizardV2OrganizationDropdownSearch_menu_list';
        return this.readDataFromElement(menuListId);
    };

    private readDataFromElement = (selectionListId: string): wizardData => {
        const ul = document.getElementById(selectionListId);
        if (!ul) {
            console.error(`cannot find element with id ${selectionListId}`);
            return null;
        }
        const data = ul.dataset.wizarddata;
        if (!data) {
            return {
                organizations: []
            } as wizardData;
        }
        return JSON.parse(data);
    }

    private writeSelectSystemDataToElement = (data: wizardData): void => {
        const selectionListId = 'subscriptionWizardV2SelectSystem_orga_selection_list';
        return this.writeDataToElement(selectionListId, data);
    };

    private writeCreateSystemDataToElement = (data: wizardData): void => {
        const menuListId = 'subscriptionWizardV2OrganizationDropdownSearch_menu_list';
        this.writeDataToElement(menuListId, data);
    };

    private writeDataToElement = (selectionListId: string, data: wizardData): void => {
        const ul = document.getElementById(selectionListId);
        if (!ul) {
            console.error(`cannot find element with id ${selectionListId}`);
            return
        }
        ul.dataset.wizarddata = JSON.stringify(data)
    }

    private clearWizardDataInElement = (): void => {
        const selectionListId = 'subscriptionWizardV2SelectSystem_orga_selection_list';
        const ul = document.getElementById(selectionListId);
        if (!ul) {
            console.error(`cannot find element with id ${selectionListId}`);
            return
        }
        ul.dataset.wizarddata = '';
        ul.dataset.selectedorganization = '';
        ul.dataset.selectedtenant = '';
        ul.dataset.selectedtenantdomain = '';
    }

    private customerInfoDtoFromState = (state: WizardState): CustomerInfoDto => {
        const info = state.customerInfo;
        return <CustomerInfoDto>{
            firstname: info.firstname,
            lastname: info.lastname,
            company: info.company,
            country: info.country,
            phone: info.phone,
            employeesCount: this.employeesCountKeyToValue(info.employeesCount)
        }
    };

    private elementScrolledTop = (el: HTMLElement): boolean => {
        return el.scrollTop == 0;
    };

    private elementScrolledBottom = (el: HTMLElement): boolean => {
        return el.scrollTop === (el.scrollHeight - el.offsetHeight);
    };

    private elementScrollable = (el: HTMLElement): boolean => {
        return el.scrollHeight != el.offsetHeight;
    };

    private elementScrollToTop = (el: HTMLElement): void => {
        el.scrollTo(0, 0);
    };

    private elementScrollToBottom = (el: HTMLElement): void => {
        el.scrollTop = el.scrollHeight - el.offsetHeight;
    };

    private employeesCountKeyToValue = (ec: EmployeesCount): string => {
        switch (ec) {
            case EmployeesCount.None:
                return "0";
            case EmployeesCount.LessThan50:
                return "1-49";
            case EmployeesCount.LessThan250:
                return "50-249";
            case EmployeesCount.Over250:
                return "249+";
        }
    };

    private employeesCountKeyToIndex = (ec: EmployeesCount): number => {
        switch (ec) {
            case EmployeesCount.None:
                return 0;
            case EmployeesCount.LessThan50:
                return 1;
            case EmployeesCount.LessThan250:
                return 2;
            case EmployeesCount.Over250:
                return 3;
        }
    };

    private employeesCountValueToKey = (value: string): EmployeesCount => {
        //todo: there might be a safer way
        return <EmployeesCount>value;
    };

    private getRequiredTextFieldsForParent = (parentId: string): mdc.textField.MDCTextField[] => {
        const mandatoryFields: mdc.textField.MDCTextField[] = [];
        const parent = <HTMLElement>document.getElementById(parentId);
        if (!parent) {
            console.debug("parent element not found");
            return mandatoryFields;
        }
        const requiredFields = Array.apply(null, parent.querySelectorAll('.mdc-text-field__input[required]'));
        requiredFields.forEach((el: HTMLElement) => {
            if (!el.id) {
                console.debug("element has no id");
                return;
            }
            const m = this._mdcTextFields[el.id];
            if (!m) {
                console.debug("no material instance found");
                return;
            }
            mandatoryFields.push(m);
        });
        return mandatoryFields;
    };

    private getDomainSuggestion = (): string => {
        if (!this._wizardState.customerInfo || !this._wizardState.customerInfo.company) {
            console.debug("getDomainSuggestion: state attribute 'company' not found or empty:", this._wizardState);
            return "";
        }
        const organization = this._wizardState.customerInfo.company;
        return this.getValidDomainString(organization);
    };

    private getValidDomainString = (s: string): string => {
        let validDomain = s.toLowerCase().trim();
        validDomain = this.removeCorporationSuffixes(validDomain);
        validDomain = this.replaceLanguageSpecificSpecialCharacters(validDomain);
        validDomain = this.removeSpecialCharacters(validDomain);
        validDomain = this.replaceWhiteSpace(validDomain);
        validDomain = this.removeExcessiveHyphen(validDomain);
        return validDomain;
    };

    private removeCorporationSuffixes = (s: string): string => {
        const suffixes = [
            " gbr",
            " gmbh",
            " mbh",
            " ohg",
            " kg",
            " ag",
            " ug",
            " partg",
            " eg",
            " epg",
            " epc",
            " co",
            " corp",
            " llc",
            " ltd",
            " inc"
        ];
        let i = 0;
        const len = suffixes.length;
        s = s.toLowerCase();
        while (i < len) {
            const reverseSuffix = suffixes[i].toLowerCase().split("").reverse().join("");
            const reverseString = s.split("").reverse().join("");
            s = reverseString.replace(reverseSuffix, "").split("").reverse().join("");
            i++;
        }
        return s.trim();
    };

    private removeSpecialCharacters = (s: string): string => {
        return s.replace(/[^a-zA-Z \-]/g, "");
    };

    private replaceLanguageSpecificSpecialCharacters = (s: string): string => {
        const replacements = {
            'ae': 'ä|Ä|æ|Æ',
            'oe': 'ö|Ö',
            'ue': 'ü|Ü',
            'a': 'á|à|ã|â|å|À|Á|Ã|Â|Å',
            'e': 'é|è|ê|ë|É|È|Ê|Ë',
            'i': 'í|ì|î|ï|Í|Ì|Î|Ï',
            'o': 'ó|ò|ô|õ|ø|Ó|Ò|Ô|Õ|Ø',
            'u': 'ú|ù|û|Ú|Ù|Û',
            'c': 'ç|Ç',
            'n': 'ñ|Ñ'
        };
        for (const key in replacements) {
            s = s.replace(new RegExp(replacements[key], 'g'), key);
        }
        return s;
    };

    private replaceWhiteSpace = (s: string): string => {
        return this.replaceAll(s, " ", "-");
    };

    private removeExcessiveHyphen = (s: string): string => {
        const len = s.length;
        let replace = false;
        const chars: string[] = [];
        for (let i = 0; i < len; i++) {
            const c = s.charAt(i);
            if (c == "-" && replace) {
                chars.push("*");
            } else {
                chars.push(c);
                replace = c == "-";
            }
        }
        s = chars.join("");
        s = this.replaceAll(s, "*", "");
        s = this.removeLeadingAndTrailingHyphen(s);
        return s
    };

    private removeLeadingAndTrailingHyphen = (s: string): string => {
        const start = s.charAt(0) == "-" ? 1 : 0;
        const end = s.charAt(s.length - 1) == "-" ? s.length - 1 : s.length;
        return s.substring(start, end);
    };

    private replaceAll = (s: string, sub: string, rep: string): string => {
        return s.split(sub).join(rep);
    };

    private setStorageForPreselectedOrganizationUpdateEvent = () => {
        if (this._withPreselectedOrganization) {
            window.localStorage.setItem('createTenantForOrganization', this._wizardState.organization);
        }
    };

    //mark: google analytics
    private dispatchGoogleTagManagerEvent = () => {
        if (!this._gtmActive || this._isSysAdmin || this._wizardState.app == 'basis') {
            return;
        }

        const affiliate = UrlHelpers.getQueryParameter(window.location.href, 'affiliate') || 'cloudcenter';

        const transactionProducts = this.getGTMTransactions(affiliate);
        if (transactionProducts.length < 1) {
            return;
        }

        const transactionId = `${this._wizardState.tenant}-${this.getTimestamp()}`;

        //dataLayer is no default property of window and thus will provoke an error. the property is added by code from the page head on load
        //
        // @ts-ignore
        window.dataLayer = window.dataLayer || [];
        const event = {
            'event': 'purchase',
            'transaction_Id': transactionId,    // Transaction ID. Required  for purchases and refunds.
            'value': 0.00,                      // Total transaction value (incl. tax and shipping)
            'currency': "EUR",            
            'items': transactionProducts
        };
        // @ts-ignore
        window.dataLayer.push(event);
    };

    private dispatchGooglePageView = (target: WizardPages) => {
        if (!this._gtmActive || this._isSysAdmin) {
            return;
        }

        const mapping = this.getGoogleAnalyticsKey(target);
        console.log(`setting page for "${target}" as "${mapping.pagePath}"`);
        //dataLayer is no default property of window and thus will provoke an error. the property is added by code from the page head on load
        // @ts-ignore
        window.dataLayer = window.dataLayer || [];
        // @ts-ignore
        window.dataLayer.push({
            'event': 'Pageview',
            'pagePath': mapping.pagePath,
            'pageTitle': mapping.pageTitle
        });
    };

    private getGTMTransactions = (affiliation: string): { [id: string]: string | number }[] => {
        const items = [];
        items.push({
            'item_id': this._wizardState.app,
            'item_name': this.getAppDisplayName(),
            'item_category': 'Allgemein',
            'affiliation': affiliation,
            'price': 0.00,
            'quantity': 1
        });
        if (this._createNewTenant) {
            items.push({
                'item_id': 'basis',
                'item_name': 'd.velop cloud Plattform Basis',
                'item_category': 'Allgemein',
                'affiliation': affiliation,
                'price': 0.00,
                'quantity': 1
            });
        }
        return items;
    };

    private getGoogleAnalyticsKey = (pageKey: WizardPages): GoogleTagManagerMapping => {
        let keyMap: { [key: string]: GoogleTagManagerMapping } = {};
        keyMap[WizardPages.SelectSystem] = {pagePath: '/center/subscription/selectTenant', pageTitle: 'Select Tenant'};
        keyMap[WizardPages.CustomerInfo] = {pagePath: '/center/subscription/customerInfo', pageTitle: 'Customer Info'}; //mark: new
        keyMap[WizardPages.CreateSystem] = {pagePath: '/center/subscription/newTenant', pageTitle: 'New Tenant'};
        keyMap[WizardPages.ShoppingCart] = {pagePath: '/center/subscription/summary', pageTitle: 'Summary'};
        keyMap[WizardPages.Final] = {pagePath: '/center/subscription/finish', pageTitle: 'Finish'};
        return keyMap[pageKey] || {pagePath: '/', pageTitle: 'unknown'};
    };

    private getTimestamp = (): string => {
        const d = new Date();
        const year = d.getFullYear().toString();
        const month = d.getMonth() + 1 < 10 ? "0" + (d.getMonth() + 1).toString() : (d.getMonth() + 1).toString();
        const day = d.getDate() < 10 ? "0" + d.getDate().toString() : d.getDate().toString();
        const hours = d.getHours() < 10 ? "0" + d.getHours().toString() : d.getHours().toString();
        const minutes = d.getMinutes() < 10 ? "0" + d.getMinutes().toString() : d.getMinutes().toString();
        const seconds = d.getSeconds() < 10 ? "0" + d.getSeconds().toString() : d.getSeconds().toString();
        const milliseconds = d.getMilliseconds() < 10 ? "0" + d.getMilliseconds().toString() : d.getMilliseconds().toString();
        return (year + month + day + hours + minutes + seconds + milliseconds.slice(0, 2));
    };

    private getAppDisplayName = (): string => {
        return (<HTMLElement>document.getElementById('subscriptionWizardV2ShoppingCart_app_displayName')).dataset.appname
    };

    private retryableFetch = (req: Request, retries: number): Promise<any> => {
        return new Promise((resolve, reject) => {
            fetch(req)
                .then(r => {
                    if (r.ok) {
                        r.json().then(d => resolve(d)).catch(err => reject(err));
                    } else if (r.status === 429) {
                        if (retries > 0) {
                            this.showLongerExecutionMessage();

                            const retryAfterValue = r.headers.get('Retry-After');

                            let retryAfter = parseInt(retryAfterValue);
                            console.info(`too many request to create tenant. Auto retry after ${retryAfter} seconds ...`);

                            setTimeout(() => {
                                console.log(`send create tenant request again...`);
                                this.retryableFetch(req, retries - 1)
                                    .then(d => {
                                        resolve(d);
                                    })
                                    .catch(err => {
                                        reject(err);
                                    });
                            }, retryAfter * 1000);
                        } else {
                            reject(`too many retries`);
                        }
                    } else {
                        reject(`failed with status ${r.status}`);
                    }
                })
                .catch(error => {
                    reject(error);
                });
        });
    };
}

interface WizardState {
    domain: string
    organization: string
    organizationName: string
    tenant: string
    customerInfo: {
        firstname: string
        lastname: string
        phone: string
        country: string
        company: string
        employeesCount: EmployeesCount
    }
    app: string
    order: string
    orderedApps: string[]
}

interface CustomerInfoDto {
    firstname: string
    lastname: string
    phone: string
    country: string
    company: string
    employeesCount: string
}

interface GoogleTagManagerMapping {
    pagePath: string,
    pageTitle: string
}

enum EmployeesCount {
    None = "none",
    LessThan50 = "small",
    LessThan250 = "medium",
    Over250 = "large"
}

enum WizardPages {
    CreateSystem = 'subscriptionWizardV2CreateSystem',
    SelectSystem = 'subscriptionWizardV2SelectSystem',
    CustomerInfo = 'subscriptionWizardV2CustomerInfo',
    ShoppingCart = 'subscriptionWizardV2ShoppingCart',
    Final = 'subscriptionWizardV2Final',
}

interface wizardData {
    _links: { [key: string]: linkRel }
    organizations: wizardOrga[]
}

interface linkRel {
    href: string
}

interface wizardOrga {
    organizationId: string
    organizationName: string
    userIsAdmin: boolean
    tenants: wizardTenant[]
}

interface wizardTenant {
    tenantId: string
    tenantDisplayName: string
    tenantDomain: string
    terminationState: TenantTerminationState
    isAppAlreadySubscribed: boolean
}

interface organizationList {
    _links: { [key: string]: linkRel }
    organizations: organizationItem[]
}

interface organizationItem {
    id: string
    name: string
}
